// Template for Global Commitment Individual Ppu Profile page

import React from 'react';
import styled from '@emotion/styled/macro';
import siteVars from '../variables';
import vars from '@variables';
import Layout from '@layouts/layout';
import SectionLayout from '@layouts/section-layout';
import ProfileContainer from '@components/global-commitment/gc-profile-container/gc-profile-container';
import HorizontalTable from '@components/horizontal-table/horizontal-table';
import SecondaryNav from '@components/secondary-nav/secondary-nav';
import TableRow from '@components/horizontal-table/table-row';
import GroupedTablesContainer from '@components/global-commitment/grouped-table-container/grouped-table-container';
import GroupedTable from '@components/grouped-table/grouped-table';
import MatrixTable from '@components/matrix-table/matrix-table';
import MatrixTableRow from '@components/matrix-table/matrix-table-row';
import MatrixTableCell from '@components/matrix-table/matrix-table-cell';
import MetricsFramework from '@components/key-metrics/metrics-framework';
import MetricsColumn from '@components/key-metrics/metrics-column';
import MetricsRow from '@components/key-metrics/metrics-row';
import VerticalBarGraph from '../design-system/src/components/vertical-bar-graph/vertical-bar-graph';
import VisualisationBlock from '@blocks/visualisation-bock/visualisation-block';
import PercentageChange from '@components/table/sub-components/percentage-change';
import createGcMetaData from '../utils/global-commitment/gc-metadata';
import SubHeader from '@components/global-commitment/sub-header/sub-header';
import FormattedText from '@components/global-commitment/formatted-text/formatted-text';
import { graphql } from 'gatsby';
import { formatNumberWithCommas } from '@utils/utils';
import { tableColumnHeaders } from '../data/global-commitment-report-2021/ppu-data.data';
import { PAtom, SubHeaderAtom, BoldPAtom } from '@atoms/text-atoms/text-atoms';
import { convertStringToBoolean, convertStringToFloat } from '@utils/utils';
import { FlexibleLinkAtom } from '@atoms/link-atoms/link-atoms';
import { transformTags } from '../utils/transform-data-functions.js';
import { getFooterData } from '../utils/translation-functions';
import {
  generateGcDropdownLinks,
  buildBarData,
} from '../utils/global-commitment/helper-functions';

export const query = graphql`
  query GcPpuPageQuery($id: String) {
    categoryLinks: allGcPpuDataCsv {
      nodes {
        organisation_name__b_1
        slug__b_4
      }
    }
    gcPpuPage: gcPpuDataCsv(id: { eq: $id }) {
      id
      category__b_0
      organisation_name__b_1
      slug__b_4
      meta_title__b_6_1
      meta_description__b_6_2
      previous_report_url__b_5
      first_year__rep
      _12_months_time_frame_for_quantitative_data__2_1
      _2019_postconsumer_recycled_content__percent___3_2_5
      _2019_recyclable_plastic_packaging__3_2_11
      _2019_reusable_plastic_packaging__percent___3_2_12
      _2019_reusable_recyclable_or_compostable__percent___3_2_17
      _2020_compostable__4_5_25
      _2020_fossilbased_virgin_content__4_4_15
      _2020_postconsumer_recycled_content__percent___4_4_11
      _2020_preconsumer_recycled_content__4_4_12
      _2020_recyclable__4_5_24
      _2020_recyclable_alternative__4_5_24_excep
      _2020_reusable_plastic_packaging__percent___4_5_13
      _2020_reusable_recyclable_or_compostable__alternative__4_5_1_excep
      _2020_reusable_recyclable_or_compostable__percent___4_5_1
      _2020_virgin_renewable_content__4_4_14
      _2025_target__rrc_full_target
      _2025_target__rrc_full_target_excep
      actions_and_progress_made_on_collaboration_towards_increasing_recycling_and_composting_rates_for_plastic_packaging__8_1_1_2_1
      actions_and_progress_made_to_make_packaging_recyclable_or_compostable__see_previous_section_for_progress_on_shifting_to_reusable_packaging___8_1_1_1_1
      activites_and_progress_made_on_elimination__6_4
      activites_and_progress_made_on_postconsumer_recycled_content__9_1
      activities_and_progress_made_over_the_reporting_period_on_reuse__7_6
      additional_details_on_investments__21_3
      additional_information_about_the_target__rt_5
      alignment_with_the_new_plastics_economy_2021_recycling_rate_survey_results__8_align
      availability_of_reuse_models_today__7_1
      bar_chart__plastic_packaging_design__alternative_text__4_5_bar_chart_alt
      bar_chart__plastic_packaging_design__display__4_5_bar_chart_display
      company_details_block__display__company_details_display
      context_to_the_plastic_reduction_target__10_4
      data_verification_assurance_status__2_2
      description__1_1
      deviation__dev
      elimination_block__display__elimination_display
      elimination_table__alternative_text__6_elimin_alt
      elimination_table__display__6_elimin_display
      ellen_macarthur_foundations_plastics_pact_network_membership__as_of_18_05_2020___2_7
      endnotes_block__display__endnotes_display
      headquarters_location__1_5
      interpreting_the_quantitative_data_submitted__4_14
      investment_block__display__investment_display
      joined_the_global_commitment__join_date
      key_metrics_block__display__key_metrics_display
      link_to_other_published_data__4_15
      long_target_description__rt_text_long
      not_aligned_caption__dev_note
      optional_metric__plastic_packaging_designed_for_recycling__4_12
      organisation_name_sortable__b_1_bis
      other_2025_reuse_goals__7_11
      pcr_block__display__pcr_display
      percent_post_consumer_recycled_content_2018__3_2_4
      percent_recyclable_reusable_and_compostable_2018___3_2_16
      pie_chart__material_sourcing__alternative_text__4_4_pie_chart_alt
      pie_chart__material_sourcing__display__4_4_pie_chart_display
      pilots_launched__7_2
      planned_actions_on_collaborating_towards_increasing_reyclings_and_composting_rates_for_plastic_packaging__8_2_1_2_1
      planned_actions_on_elimination__6_7
      planned_actions_on_reuse__7_15
      planned_actions_to_increase_postconsumer_recycled_content__9_2
      planned_actions_to_make_packaging_recyclable_or_compostable__8_2_1_1_1
      plastic_packaging_categories_in_portfolio__alternative_text__4_3_tables_alt
      plastic_packaging_categories_in_portfolio__display_tables__4_3_tables_display
      plastic_packaging_designed_for_recycling__4_13
      plastic_packaging_portfolio_details_block__display__portfolio_display
      plastic_packaging_weight_baseline__rt_4
      postconsumer_recycled_content__alternative__4_4_11_excep
      product_line_definition__prod_line_def
      products_sold_in_plastic_packaging__5_1
      publish_report__b_3
      reason_for_scope_exclusion_in_the_reduction_target_if_any__10_3
      recyclability_assessment_evidence_for_alternative_assumptions_used__8_endnotes
      reduction_target__text__rt_text_short
      reduction_target__rank_on_total_targets__rank_total
      reduction_target__full_sentence__rt_full
      reduction_target__rank_on_virgin_targets__rank_virgin
      reduction_target__type__rt_type
      reduction_target_block__display__reduction_target_display
      regions_active__1_6
      reporting_details_block__display__reporting_details_display
      reusable_packaging__alternative__4_5_13_excep
      reuse_block__display__reuse_display
      reuse_table__alternative_text__7_5_reuse_alt
      reuse_table__display__7_5_reuse_display
      reuse_to_be_introduced_table__alternative_text__7_10_r_plan_alt
      reuse_to_be_introduced_table__display__7_10_r_plan_display
      reused_plastic_packaging_weight__metric_tonnes___4_1_2
      revenue_band__usd___1_4_2_1
      revenues_to_be_derived_from_reuse_models_by_2025__7_13_1_text
      rigids_table__alternative_text__4_3_rigids_alt
      rigids_table__display__4_3_rigids_display
      rrc_block__display__rrc_display
      scope_of_reported_data__2_5
      scope_of_reporting__2_6
      sector__b_2
      target_postconsumer_recycled_content__alternative__pcr_target_excep
      target_postconsumer_recycled_content__percent___label__pcr_target_label
      target_postconsumer_recycled_content__percent___pcr_target
      target_reusable_recyclable_and_or_compostable__alternative__rrc_target_excep
      target_reusable_recyclable_and_or_compostable__label__rrc_target_label
      target_reusable_recyclable_and_or_compostable__rrc_target
      thirdparty_verification_assurance__2_3
      to_be_eliminated_table__alternative_text__6_6_tbe_alt
      to_be_eliminated_table__display__6_6_tbe_display
      total_invested_committed_over_the_reporting_period__21_1_1
      total_invested_committed_over_the_reporting_period__alternative_text__21_2
      units_solds_to_be_derived_from_reuse_models_by_2025__7_14_5_text
      visual_1__rrc___alternative_text__v1_alt
      visual_1__rrc___display__v1_display
      visual_2__pcr___alternative_text__v2_alt
      visual_2__pcr___display__v2_display
      webpage_or_report_addressing_progress__1_3
      website__1_2
      weight_new_plastic_packaging__alternative__4_2
      weight_new_plastic_packaging__metrics_tonnes___4_1_1
    }
    tableData__4_3: gcPpuDataCsv(id: { eq: $id }) {
      rigids_table__alternative_text__4_3_rigids_alt
      rigids_table__display__4_3_rigids_display
      pet_bottle__4_3_1_1_1
      pet_bottle__percent__4_3_2_1_1
      pet_thermoforms__4_3_1_2_1
      pet_thermoforms__percent__4_3_2_2_1
      other_pet_rigids__4_3_1_3_1
      other_pet_rigids__percent__4_3_2_3_1
      hdpe_bottles__4_3_1_4_1
      hdpe_bottles__percent__4_3_2_4_1
      hdpe_other_rigids__4_3_1_5_1
      hdpe_other_rigids__percent__4_3_2_5_1
      pp_bottles__4_3_1_6_1
      pp_bottles__percent__4_3_2_6_1
      pp_other_rigids__4_3_1_7_1
      pp_other_rigids__percent__4_3_2_7_1
      pe_tubes__4_3_1_8_1
      pe_tubes__percent__4_3_2_8_1
      ps_rigids__4_3_1_9_1
      ps_rigids__percent__4_3_2_9_1
      eps_rigids__4_3_1_10_1
      eps_rigids__percent__4_3_2_10_1
      pvc_rigids__4_3_1_11_1
      pvc_rigids__percent__4_3_2_11_1
      flexibles_table__alternative_text__4_3_flexibles_alt
      flexibles_table__display__4_3_flexibles_display
      _a4_flexibles_pe__4_3_1_15_1
      _a4_flexibles_pe__percent__4_3_2_15_1
      _a4_flexibles_pp__4_3_1_16_1
      _a4_flexibles_pp__percent__4_3_2_16_1
      _a4_multimaterial_flexibles__4_3_1_17_1
      _a4_multimaterial_flexibles__percent__4_3_2_17_1
      _a4_monomaterials_pe_in_b2b_context__4_3_1_12_1
      _a4_monomaterials_pe_in_b2b_context__percent__4_3_2_12_1
      _a4_monomaterials_pe_in_b2c_context__4_3_1_13_1
      _a4_monomaterials_pe_in_b2c_context__percent__4_3_2_13_1
      other__a4_flexibles__4_3_1_14_1
      other__a4_flexibles__percent__4_3_2_14_1
      other__a4_monomaterial_flexibles__4_3_1_18_1
      other__a4_monomaterial_flexibles__percent__4_3_2_18_1
      other_table__alternative_text__4_3_other_alt
      other_table__display__4_3_other_display
      other__4_3_1_19_1
      other__description__4_3_1_19_text
      other__percent__4_3_2_19_1
    }
    tableData__6_1: gcPpuDataCsv(id: { eq: $id }) {
      material_10__means_of_elimination__6_1_2_10
      material_10__name__6_1_4_10
      material_10__other_details__6_1_5_10_1
      material_10__percent_eliminated__6_1_3_10_1
      material_10__quantity_eliminated__6_1_10
      material_10__weight_eliminated__6_1_1_10_1
      material_1__means_of_elimination__6_1_2_1
      material_1__name__6_1_4_1
      material_1__other_details__6_1_5_1_1
      material_1__percent_eliminated__6_1_3_1_1
      material_1__quantity_eliminated__6_1_1
      material_1__weight_eliminated__6_1_1_1_1
      material_2__means_of_elimination__6_1_2_2
      material_2__name__6_1_4_2
      material_2__other_details__6_1_5_2_1
      material_2__percent_eliminated__6_1_3_2_1
      material_2__quantity_eliminated__6_1_2
      material_2__weight_eliminated__6_1_1_2_1
      material_3__means_of_elimination__6_1_2_3
      material_3__name__6_1_4_3
      material_3__other_details__6_1_5_3_1
      material_3__percent_eliminated__6_1_3_3_1
      material_3__quantity_eliminated__6_1_3
      material_3__weight_eliminated__6_1_1_3_1
      material_4__means_of_elimination__6_1_2_4
      material_4__name__6_1_4_4
      material_4__other_details__6_1_5_4_1
      material_4__percent_eliminated__6_1_3_4_1
      material_4__quantity_eliminated__6_1_4
      material_4__weight_eliminated__6_1_1_4_1
      material_5__means_of_elimination__6_1_2_5
      material_5__name__6_1_4_5
      material_5__other_details__6_1_5_5_1
      material_5__percent_eliminated__6_1_3_5_1
      material_5__quantity_eliminated__6_1_5
      material_5__weight_eliminated__6_1_1_5_1
      material_6__means_of_elimination__6_1_2_6
      material_6__name__6_1_4_6
      material_6__other_details__6_1_5_6_1
      material_6__percent_eliminated__6_1_3_6_1
      material_6__quantity_eliminated__6_1_6
      material_6__weight_eliminated__6_1_1_6_1
      material_7__means_of_elimination__6_1_2_7
      material_7__name__6_1_4_7
      material_7__other_details__6_1_5_7_1
      material_7__percent_eliminated__6_1_3_7_1
      material_7__quantity_eliminated__6_1_7
      material_7__weight_eliminated__6_1_1_7_1
      material_8__means_of_elimination__6_1_2_8
      material_8__name__6_1_4_8
      material_8__other_details__6_1_5_8_1
      material_8__percent_eliminated__6_1_3_8_1
      material_8__quantity_eliminated__6_1_8
      material_8__weight_eliminated__6_1_1_8_1
      material_9__means_of_elimination__6_1_2_9
      material_9__name__6_1_4_9
      material_9__other_details__6_1_5_9_1
      material_9__percent_eliminated__6_1_3_9_1
      material_9__quantity_eliminated__6_1_9
      material_9__weight_eliminated__6_1_1_9_1
    }
    tableData__6_2: gcPpuDataCsv(id: { eq: $id }) {
      format_10__means_of_elimination__6_2_2_10
      format_10__name__6_2_4_10
      format_10__other_details__6_2_5_10_1
      format_10__percent_eliminated__6_2_3_10_1
      format_10__quantity_eliminated__6_2_1_10_2
      format_10__weight_eliminated__6_2_1_10_1
      format_1__means_of_elimination__6_2_2_1
      format_1__name__6_2_4_1
      format_1__other_details__6_2_5_1_1
      format_1__percent_eliminated__6_2_3_1_1
      format_1__quantity_eliminated__6_2_1_1_2
      format_1__weight_eliminated__6_2_1_1_1
      format_2__means_of_elimination__6_2_2_2
      format_2__name__6_2_4_2
      format_2__other_details__6_2_5_2_1
      format_2__percent_eliminated__6_2_3_2_1
      format_2__quantity_eliminated__6_2_1_2_2
      format_2__weight_eliminated__6_2_1_2_1
      format_3__means_of_elimination__6_2_2_3
      format_3__name__6_2_4_3
      format_3__other_details__6_2_5_3_1
      format_3__percent_eliminated__6_2_3_3_1
      format_3__quantity_eliminated__6_2_1_3_2
      format_3__weight_eliminated__6_2_1_3_1
      format_4__means_of_elimination__6_2_2_4
      format_4__name__6_2_4_4
      format_4__other_details__6_2_5_4_1
      format_4__percent_eliminated__6_2_3_4_1
      format_4__quantity_eliminated__6_2_1_4_2
      format_4__weight_eliminated__6_2_1_4_1
      format_5__means_of_elimination__6_2_2_5
      format_5__name__6_2_4_5
      format_5__other_details__6_2_5_5_1
      format_5__percent_eliminated__6_2_3_5_1
      format_5__quantity_eliminated__6_2_1_5_2
      format_5__weight_eliminated__6_2_1_5_1
      format_6__means_of_elimination__6_2_2_6
      format_6__name__6_2_4_6
      format_6__other_details__6_2_5_6_1
      format_6__percent_eliminated__6_2_3_6_1
      format_6__quantity_eliminated__6_2_1_6_2
      format_6__weight_eliminated__6_2_1_6_1
      format_7__means_of_elimination__6_2_2_7
      format_7__name__6_2_4_7
      format_7__other_details__6_2_5_7_1
      format_7__percent_eliminated__6_2_3_7_1
      format_7__quantity_eliminated__6_2_1_7_2
      format_7__weight_eliminated__6_2_1_7_1
      format_8__means_of_elimination__6_2_2_8
      format_8__name__6_2_4_8
      format_8__other_details__6_2_5_8_1
      format_8__percent_eliminated__6_2_3_8_1
      format_8__quantity_eliminated__6_2_1_8_2
      format_8__weight_eliminated__6_2_1_8_1
      format_9__means_of_elimination__6_2_2_9
      format_9__name__6_2_4_9
      format_9__other_details__6_2_5_9_1
      format_9__percent_eliminated__6_2_3_9_1
      format_9__quantity_eliminated__6_2_1_9_2
      format_9__weight_eliminated__6_2_1_9_1
    }
    tableData__6_3: gcPpuDataCsv(id: { eq: $id }) {
      component_10__means_of_elimination__6_3_2_10
      component_10__name__6_3_4_10
      component_10__other_details__6_3_5_10_1
      component_10__percent_eliminated__6_3_3_10_1
      component_10__quantity_eliminated__6_3_1_10_2
      component_10__weight_eliminated__6_3_1_10_1
      component_1__means_of_elimination__6_3_2_1
      component_1__name__6_3_4_1
      component_1__other_details__6_3_5_1_1
      component_1__percent_eliminated__6_3_3_1_1
      component_1__quantity_eliminated__6_3_1_1_2
      component_1__weight_eliminated__6_3_1_1_1
      component_2__means_of_elimination__6_3_2_2
      component_2__name__6_3_4_2
      component_2__other_details__6_3_5_2_1
      component_2__percent_eliminated__6_3_3_2_1
      component_2__quantity_eliminated__6_3_1_2_2
      component_2__weight_eliminated__6_3_1_2_1
      component_3__means_of_elimination__6_3_2_3
      component_3__name__6_3_4_3
      component_3__other_details__6_3_5_3_1
      component_3__percent_eliminated__6_3_3_3_1
      component_3__quantity_eliminated__6_3_1_3_2
      component_3__weight_eliminated__6_3_1_3_1
      component_4__means_of_elimination__6_3_2_4
      component_4__name__6_3_4_4
      component_4__other_details__6_3_5_4_1
      component_4__percent_eliminated__6_3_3_4_1
      component_4__quantity_eliminated__6_3_1_4_2
      component_4__weight_eliminated__6_3_1_4_1
      component_5__means_of_elimination__6_3_2_5
      component_5__name__6_3_4_5
      component_5__other_details__6_3_5_5_1
      component_5__percent_eliminated__6_3_3_5_1
      component_5__quantity_eliminated__6_3_1_5_2
      component_5__weight_eliminated__6_3_1_5_1
      component_6__means_of_elimination__6_3_2_6
      component_6__name__6_3_4_6
      component_6__other_details__6_3_5_6_1
      component_6__percent_eliminated__6_3_3_6_1
      component_6__quantity_eliminated__6_3_1_6_2
      component_6__weight_eliminated__6_3_1_6_1
      component_7__means_of_elimination__6_3_2_7
      component_7__name__6_3_4_7
      component_7__other_details__6_3_5_7_1
      component_7__percent_eliminated__6_3_3_7_1
      component_7__quantity_eliminated__6_3_1_7_2
      component_7__weight_eliminated__6_3_1_7_1
      component_8__means_of_elimination__6_3_2_8
      component_8__name__6_3_4_8
      component_8__other_details__6_3_5_8_1
      component_8__percent_eliminated__6_3_3_8_1
      component_8__quantity_eliminated__6_3_1_8_2
      component_8__weight_eliminated__6_3_1_8_1
      component_9__means_of_elimination__6_3_2_9
      component_9__name__6_3_4_9
      component_9__other_details__6_3_5_9_1
      component_9__percent_eliminated__6_3_3_9_1
      component_9__quantity_eliminated__6_3_1_9_2
      component_9__weight_eliminated__6_3_1_9_1
    }
    tableData__6_6: gcPpuDataCsv(id: { eq: $id }) {
      b2b_rigids__6_6_4_17_1
      b2b_rigids__other_details__6_6_5_17_1
      b2b_rigids__percent_to_be_eliminated__6_6_2_17
      b2b_rigids__quantity_to_be_eliminated__6_6_1_17_2
      b2b_rigids__weight_to_be_eliminated__6_6_1_17_1
      b2b_rigids__year__6_6_3_17
      eps__6_6_4_1_1
      eps__other_details__6_6_5_1_1
      eps__percent_to_be_eliminated__6_6_2_1
      eps__quantity_to_be_eliminated__6_6_1_1_2
      eps__weight_to_be_eliminated__6_6_1_1_1
      eps__year__6_6_3_1
      film__very_small__e_g__portion_size_sachets__6_6_4_10_1
      film__very_small__e_g__portion_size_sachets__other_details__6_6_5_10_1
      film__very_small__e_g__portion_size_sachets__percent_to_be_eliminated__6_6_2_10
      film__very_small__e_g__portion_size_sachets__quantity_to_be_eliminated__6_6_1_10_2
      film__very_small__e_g__portion_size_sachets__weight_to_be_eliminated__6_6_1_10_1
      film__very_small__e_g__portion_size_sachets__year__6_6_3_10
      film_b2b__e_g__pallet_wrap__6_6_4_13_1
      film_b2b__e_g__pallet_wrap__other_details__6_6_5_13_1
      film_b2b__e_g__pallet_wrap__percent_to_be_eliminated__6_6_2_13
      film_b2b__e_g__pallet_wrap__quantity_to_be_eliminated__6_6_1_13_2
      film_b2b__e_g__pallet_wrap__weight_to_be_eliminated__6_6_1_13_1
      film_b2b__e_g__pallet_wrap__year__6_6_3_13
      film_b2c__monomaterial_clear__6_6_4_11_1
      film_b2c__monomaterial_clear__other_details__6_6_5_11_1
      film_b2c__monomaterial_clear__percent_to_be_eliminated__6_6_2_11
      film_b2c__monomaterial_clear__quantity_to_be_eliminated__6_6_1_11_2
      film_b2c__monomaterial_clear__weight_to_be_eliminated__6_6_1_11_1
      film_b2c__monomaterial_clear__year__6_6_3_11
      film_b2c__other__opaque_printed_multimaterial___6_6_4_12_1
      film_b2c__other__opaque_printed_multimaterial___other_details__6_6_5_12_1
      film_b2c__other__opaque_printed_multimaterial___percent_to_be_eliminated__6_6_2_12
      film_b2c__other__opaque_printed_multimaterial___quantity_to_be_eliminated__6_6_1_12_2
      film_b2c__other__opaque_printed_multimaterial___weight_to_be_eliminated__6_6_1_12_1
      film_b2c__other__opaque_printed_multimaterial___year__6_6_3_12
      labels_stickers_sleeves__6_6_4_22_1
      labels_stickers_sleeves__other_details__6_6_5_22_1
      labels_stickers_sleeves__percent_to_be_eliminated__6_6_2_22
      labels_stickers_sleeves__quantity_to_be_eliminated__6_6_1_22_2
      labels_stickers_sleeves__weight_to_be_eliminated__6_6_1_22_1
      labels_stickers_sleeves__year__6_6_3_22
      lids_closures__6_6_4_23_1
      lids_closures__other_details__6_6_5_23_1
      lids_closures__percent_to_be_eliminated__6_6_2_23
      lids_closures__quantity_to_be_eliminated__6_6_1_23_2
      lids_closures__weight_to_be_eliminated__6_6_1_23_1
      lids_closures__year__6_6_3_23
      metallised_films__6_6_4_2_1
      metallised_films__other_details__6_6_5_2_1
      metallised_films__percent_to_be_eliminated__6_6_2_2
      metallised_films__quantity_to_be_eliminated__6_6_1_2_2
      metallised_films__weight_to_be_eliminated__6_6_1_2_1
      metallised_films__year__6_6_3_2
      multilayer_materials__6_6_4_3_1
      multilayer_materials__other_details__6_6_5_3_1
      multilayer_materials__percent_to_be_eliminated__6_6_2_3
      multilayer_materials__quantity_to_be_eliminated__6_6_1_3_2
      multilayer_materials__weight_to_be_eliminated__6_6_1_3_1
      multilayer_materials__year__6_6_3_3
      other_1__name__6_6_4_27_text
      other_1__other_details__6_6_5_27_1
      other_1__percent_to_be_eliminated__6_6_2_27
      other_1__quantity_to_be_eliminated__6_6_1_27_2
      other_1__weight_to_be_eliminated__6_6_1_27_1
      other_1__year__6_6_3_27
      other_2__name__6_6_4_28_text
      other_2__other_details__6_6_5_28_1
      other_2__percent_to_be_eliminated__6_6_2_28
      other_2__quantity_to_be_eliminated__6_6_1_28_2
      other_2__weight_to_be_eliminated__6_6_1_28_1
      other_2__year__6_6_3_28
      other__6_6_4_27_1
      other__6_6_4_28_1
      other_pigment_additive__6_6_4_9_1
      other_pigment_additive__other_details__6_6_5_9_1
      other_pigment_additive__percent_to_be_eliminated__6_6_2_9
      other_pigment_additive__quantity_to_be_eliminated__6_6_1_9_2
      other_pigment_additive__weight_to_be_eliminated__6_6_1_9_1
      other_pigment_additive__year__6_6_3_9
      petg_in_rigid_plastic_packaging__6_6_4_4_1
      petg_in_rigid_plastic_packaging__other_details__6_6_5_4_1
      petg_in_rigid_plastic_packaging__percent_to_be_eliminated__6_6_2_4
      petg_in_rigid_plastic_packaging__quantity_to_be_eliminated__6_6_1_4_2
      petg_in_rigid_plastic_packaging__weight_to_be_eliminated__6_6_1_4_1
      petg_in_rigid_plastic_packaging__year__6_6_3_4
      plastic_windows__in_cardboard_boxes___6_6_4_24_1
      plastic_windows__in_cardboard_boxes___other_details__6_6_5_24_1
      plastic_windows__in_cardboard_boxes___percent_to_be_eliminated__6_6_2_24
      plastic_windows__in_cardboard_boxes___quantity_to_be_eliminated__6_6_1_24_2
      plastic_windows__in_cardboard_boxes___weight_to_be_eliminated__6_6_1_24_1
      plastic_windows__in_cardboard_boxes___year__6_6_3_24
      ps__6_6_4_5_1
      ps__other_details__6_6_5_5_1
      ps__percent_to_be_eliminated__6_6_2_5
      ps__quantity_to_be_eliminated__6_6_1_5_2
      ps__weight_to_be_eliminated__6_6_1_5_1
      ps__year__6_6_3_5
      pumps_trigger_sprays__6_6_4_25_1
      pumps_trigger_sprays__other_details__6_6_5_25_1
      pumps_trigger_sprays__percent_to_be_eliminated__6_6_2_25
      pumps_trigger_sprays__quantity_to_be_eliminated__6_6_1_25_2
      pumps_trigger_sprays__weight_to_be_eliminated__6_6_1_25_1
      pumps_trigger_sprays__year__6_6_3_25
      pvc__6_6_4_6_1
      pvc__other_details__6_6_5_6_1
      pvc__percent_to_be_eliminated__6_6_2_6
      pvc__quantity_to_be_eliminated__6_6_1_6_2
      pvc__weight_to_be_eliminated__6_6_1_6_1
      pvc__year__6_6_3_6
      pvdc__6_6_4_7_1
      pvdc__other_details__6_6_5_7_1
      pvdc__percent_to_be_eliminated__6_6_2_7
      pvdc__quantity_to_be_eliminated__6_6_1_7_2
      pvdc__weight_to_be_eliminated__6_6_1_7_1
      pvdc__year__6_6_3_7
      rigids_b2c__beverage_bottles__6_6_4_14_1
      rigids_b2c__beverage_bottles__other_details__6_6_5_14_1
      rigids_b2c__beverage_bottles__percent_to_be_eliminated__6_6_2_14
      rigids_b2c__beverage_bottles__quantity_to_be_eliminated__6_6_1_14_2
      rigids_b2c__beverage_bottles__weight_to_be_eliminated__6_6_1_14_1
      rigids_b2c__beverage_bottles__year__6_6_3_14
      rigids_b2c__household_personal_products_bottles__6_6_4_15_1
      rigids_b2c__household_personal_products_bottles__other_details__6_6_5_15_1
      rigids_b2c__household_personal_products_bottles__percent_to_be_eliminated__6_6_2_15
      rigids_b2c__household_personal_products_bottles__quantity_to_be_eliminated__6_6_1_15_2
      rigids_b2c__household_personal_products_bottles__weight_to_be_eliminated__6_6_1_15_1
      rigids_b2c__household_personal_products_bottles__year__6_6_3_15
      rigids_b2c__other__e_g__pots_tubs_and_trays___6_6_4_16_1
      rigids_b2c__other__e_g__pots_tubs_and_trays___other_details__6_6_5_16_1
      rigids_b2c__other__e_g__pots_tubs_and_trays___percent_to_be_eliminated__6_6_2_16
      rigids_b2c__other__e_g__pots_tubs_and_trays___quantity_to_be_eliminated__6_6_1_16_2
      rigids_b2c__other__e_g__pots_tubs_and_trays___weight_to_be_eliminated__6_6_1_16_1
      rigids_b2c__other__e_g__pots_tubs_and_trays___year__6_6_3_16
      singleuse_carrier_bags__6_6_4_18_1
      singleuse_carrier_bags__other_details__6_6_5_18_1
      singleuse_carrier_bags__percent_to_be_eliminated__6_6_2_18
      singleuse_carrier_bags__quantity_to_be_eliminated__6_6_1_18_2
      singleuse_carrier_bags__weight_to_be_eliminated__6_6_1_18_1
      singleuse_carrier_bags__year__6_6_3_18
      singleuse_cutlery_serveware__6_6_4_19_1
      singleuse_cutlery_serveware__other_details__6_6_5_19_1
      singleuse_cutlery_serveware__percent_to_be_eliminated__6_6_2_19
      singleuse_cutlery_serveware__quantity_to_be_eliminated__6_6_1_19_2
      singleuse_cutlery_serveware__weight_to_be_eliminated__6_6_1_19_1
      singleuse_cutlery_serveware__year__6_6_3_19
      singleuse_hangers__for_clothing___6_6_4_20_1
      singleuse_hangers__for_clothing___other_details__6_6_5_20_1
      singleuse_hangers__for_clothing___percent_to_be_eliminated__6_6_2_20
      singleuse_hangers__for_clothing___quantity_to_be_eliminated__6_6_1_20_2
      singleuse_hangers__for_clothing___weight_to_be_eliminated__6_6_1_20_1
      singleuse_hangers__for_clothing___year__6_6_3_20
      singleuse_straws__6_6_4_21_1
      singleuse_straws__other_details__6_6_5_21_1
      singleuse_straws__percent_to_be_eliminated__6_6_2_21
      singleuse_straws__quantity_to_be_eliminated__6_6_1_21_2
      singleuse_straws__weight_to_be_eliminated__6_6_1_21_1
      singleuse_straws__year__6_6_3_21
      tearoffs__6_6_4_26_1
      tearoffs__other_details__6_6_5_26_1
      tearoffs__percent_to_be_eliminated__6_6_2_26
      tearoffs__quantity_to_be_eliminated__6_6_1_26_2
      tearoffs__weight_to_be_eliminated__6_6_1_26_1
      tearoffs__year__6_6_3_26
      undetectable_carbon_black__6_6_4_8_1
      undetectable_carbon_black__other_details__6_6_5_8_1
      undetectable_carbon_black__percent_to_be_eliminated__6_6_2_8
      undetectable_carbon_black__quantity_to_be_eliminated__6_6_1_8_2
      undetectable_carbon_black__weight_to_be_eliminated__6_6_1_8_1
      undetectable_carbon_black__year__6_6_3_8
    }
    tableData__7_5: gcPpuDataCsv(id: { eq: $id }) {
      alcoholic_beverages__7_5_1
      alcoholic_beverages__market__7_5_2_1_1
      alcoholic_beverages__other_details__7_5_3_1_1
      alcoholic_beverages__reuse_models_in_place__7_5_1_1
      chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___7_5_2
      chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___market__7_5_2_2_1
      chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___other_details__7_5_3_2_1
      chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___reuse_models_in_place__7_5_1_2
      cleaning_products__7_5_3
      cleaning_products__market__7_5_2_3_1
      cleaning_products__other_details__7_5_3_3_1
      cleaning_products__reuse_models_in_place__7_5_1_3
      clothing___apparel__7_5_4
      clothing___apparel__market__7_5_2_4_1
      clothing___apparel__other_details__7_5_3_4_1
      clothing___apparel__reuse_models_in_place__7_5_1_4
      cosmetics___personal_care__7_5_5
      cosmetics___personal_care__market__7_5_2_5_1
      cosmetics___personal_care__other_details__7_5_3_5_1
      cosmetics___personal_care__reuse_models_in_place__7_5_1_5
      electronics__7_5_6
      electronics__market__7_5_2_6_1
      electronics__other_details__7_5_3_6_1
      electronics__reuse_models_in_place__7_5_1_6
      food___nutrition__7_5_7
      food___nutrition__market__7_5_2_7_1
      food___nutrition__other_details__7_5_3_7_1
      food___nutrition__reuse_models_in_place__7_5_1_7
      furniture_furnishings___household_items__7_5_8
      furniture_furnishings___household_items__market__7_5_2_8_1
      furniture_furnishings___household_items__other_details__7_5_3_8_1
      furniture_furnishings___household_items__reuse_models_in_place__7_5_1_8
      home_improvement___gardening__7_5_9
      home_improvement___gardening__market__7_5_2_9_1
      home_improvement___gardening__other_details__7_5_3_9_1
      home_improvement___gardening__reuse_models_in_place__7_5_1_9
      nonalcoholic_beverages__7_5_10
      nonalcoholic_beverages__market__7_5_2_10_1
      nonalcoholic_beverages__other_details__7_5_3_10_1
      nonalcoholic_beverages__reuse_models_in_place__7_5_1_10
      other__7_5_16
      other__market__7_5_2_16_1
      other__name__7_5_1_16_text
      other__other_details__7_5_3_16_1
      other__reuse_models_in_place__7_5_1_16
      pet___animal_supplies__7_5_11
      pet___animal_supplies__market__7_5_2_11_1
      pet___animal_supplies__other_details__7_5_3_11_1
      pet___animal_supplies__reuse_models_in_place__7_5_1_11
      pharmaceutical___medical_goods__7_5_12
      pharmaceutical___medical_goods__market__7_5_2_12_1
      pharmaceutical___medical_goods__other_details__7_5_3_12_1
      pharmaceutical___medical_goods__reuse_models_in_place__7_5_1_12
      productagnostic_packaging__e_g__shrink_wrap_pallets___7_5_13
      productagnostic_packaging__e_g__shrink_wrap_pallets___market__7_5_2_13_1
      productagnostic_packaging__e_g__shrink_wrap_pallets___other_details__7_5_3_13_1
      productagnostic_packaging__e_g__shrink_wrap_pallets___reuse_models_in_place__7_5_1_13
      tobacco__7_5_14
      tobacco__market__7_5_2_14_1
      tobacco__other_details__7_5_3_14_1
      tobacco__reuse_models_in_place__7_5_1_14
      toys__7_5_15
      toys__market__7_5_2_15_1
      toys__other_details__7_5_3_15_1
      toys__reuse_models_in_place__7_5_1_15
    }
    tableData__7_10: gcPpuDataCsv(id: { eq: $id }) {
      alcoholic_beverages__7_10_1
      alcoholic_beverages__market_in_which_it_will_be_introduced__7_10_2_1_1
      alcoholic_beverages__other_details__7_10_3_1_1
      alcoholic_beverages__reuse_models_to_be_introduce__7_10_1_1
      chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___7_10_2
      chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___market_in_which_it_will_be_introduced__7_10_2_2_1
      chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___other_details__7_10_3_2_1
      chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___reuse_models_to_be_introduce__7_10_1_2
      cleaning_products__7_10_3
      cleaning_products__market_in_which_it_will_be_introduced__7_10_2_3_1
      cleaning_products__other_details__7_10_3_3_1
      cleaning_products__reuse_models_to_be_introduce__7_10_1_3
      clothing___apparel__7_10_4
      clothing___apparel__market_in_which_it_will_be_introduced__7_10_2_4_1
      clothing___apparel__other_details__7_10_3_4_1
      clothing___apparel__reuse_models_to_be_introduce__7_10_1_4
      cosmetics___personal_care__7_10_5
      cosmetics___personal_care__market_in_which_it_will_be_introduced__7_10_2_5_1
      cosmetics___personal_care__other_details__7_10_3_5_1
      cosmetics___personal_care__reuse_models_to_be_introduce__7_10_1_5
      electronics__7_10_6
      electronics__market_in_which_it_will_be_introduced__7_10_2_6_1
      electronics__other_details__7_10_3_6_1
      electronics__reuse_models_to_be_introduce__7_10_1_6
      food___nutrition__7_10_7
      food___nutrition__market_in_which_it_will_be_introduced__7_10_2_7_1
      food___nutrition__other_details__7_10_3_7_1
      food___nutrition__reuse_models_to_be_introduce__7_10_1_7
      furniture_furnishings___household_items__7_10_8
      furniture_furnishings___household_items__market_in_which_it_will_be_introduced__7_10_2_8_1
      furniture_furnishings___household_items__other_details__7_10_3_8_1
      furniture_furnishings___household_items__reuse_models_to_be_introduce__7_10_1_8
      home_improvement___gardening__7_10_9
      home_improvement___gardening__market_in_which_it_will_be_introduced__7_10_2_9_1
      home_improvement___gardening__other_details__7_10_3_9_1
      home_improvement___gardening__reuse_models_to_be_introduce__7_10_1_9
      nonalcoholic_beverages__7_10_10
      nonalcoholic_beverages__market_in_which_it_will_be_introduced__7_10_2_10_1
      nonalcoholic_beverages__other_details__7_10_3_10_1
      nonalcoholic_beverages__reuse_models_to_be_introduce__7_10_1_10
      other__7_10_16
      other__market_in_which_it_will_be_introduced__7_10_2_16_1
      other__name__7_10_1_16_text
      other__other_details__7_10_3_16_1
      other__reuse_models_to_be_introduce__7_10_1_16
      pet___animal_supplies__7_10_11
      pet___animal_supplies__market_in_which_it_will_be_introduced__7_10_2_11_1
      pet___animal_supplies__other_details__7_10_3_11_1
      pet___animal_supplies__reuse_models_to_be_introduce__7_10_1_11
      pharmaceutical___medical_goods__7_10_12
      pharmaceutical___medical_goods__market_in_which_it_will_be_introduced__7_10_2_12_1
      pharmaceutical___medical_goods__other_details__7_10_3_12_1
      pharmaceutical___medical_goods__reuse_models_to_be_introduce__7_10_1_12
      productagnostic_packaging__e_g__shrink_wrap_pallets___7_10_13
      productagnostic_packaging__e_g__shrink_wrap_pallets___market_in_which_it_will_be_introduced__7_10_2_13_1
      productagnostic_packaging__e_g__shrink_wrap_pallets___other_details__7_10_3_13_1
      productagnostic_packaging__e_g__shrink_wrap_pallets___reuse_models_to_be_introduce__7_10_1_13
      tobacco__7_10_14
      tobacco__market_in_which_it_will_be_introduced__7_10_2_14_1
      tobacco__other_details__7_10_3_14_1
      tobacco__reuse_models_to_be_introduce__7_10_1_14
      toys__7_10_15
      toys__market_in_which_it_will_be_introduced__7_10_2_15_1
      toys__other_details__7_10_3_15_1
      toys__reuse_models_to_be_introduce__7_10_1_15
    }
  }
`;

const Page = ({ data, className, pageContext }) => {
  // Primary nav and footer data handling.
  const footerData = getFooterData('en-GB');
  const pageData = data.gcPpuPage;
  const tableHeadGrouped = tableColumnHeaders.groupedTable;
  const tableHeadGroupedNoLabels = tableColumnHeaders.groupedTableNoLabels;

  const companySecondaryNavData = [
    {
      isVisible: convertStringToBoolean(
        pageData.key_metrics_block__display__key_metrics_display
      ),
      title: 'key metrics',
      url: '#key-metrics',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.company_details_block__display__company_details_display
      ),
      title: 'company details',
      url: '#company-details',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.reporting_details_block__display__reporting_details_display
      ),
      title: 'reporting details',
      url: '#reporting-details',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.plastic_packaging_portfolio_details_block__display__portfolio_display
      ),
      title: 'portfolio',
      url: '#portfolio',
    },
    {
      isVisible: true,
      title: 'commitments',
      url: '#commitments',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.pcr_block__display__pcr_display
      ),
      title: 'investments',
      url: '#investments',
    },
    {
      isVisible: convertStringToBoolean(
        pageData.endnotes_block__display__endnotes_display
      ),
      title: 'end notes',
      url: '#end-notes',
    },
  ].filter(element => element.isVisible === true);

  const tableHeadElimination = tableColumnHeaders.eliminationTable;
  const tableHeadPlannedActions = tableColumnHeaders.plannedActionsTable;
  const tableHeadReuse = tableColumnHeaders.reuseTable;
  const tableHeadreusePlanneActions =
    tableColumnHeaders.reusePlanneActionsTable;
  const tableData__4_3 = data.tableData__4_3;
  const tableData__6_1 = data.tableData__6_1;
  const tableData__6_2 = data.tableData__6_2;
  const tableData__6_3 = data.tableData__6_3;
  const tableData__6_6 = data.tableData__6_6;
  const tableData__7_5 = data.tableData__7_5;
  const tableData__7_10 = data.tableData__7_10;

  const rigidsGroupData = [
    {
      isVisible: convertStringToBoolean(tableData__4_3.pet_bottle__4_3_1_1_1),
      category: 'PET bottle',
      percentage: tableData__4_3.pet_bottle__percent__4_3_2_1_1,
    },
    {
      isVisible: convertStringToBoolean(
        tableData__4_3.pet_thermoforms__4_3_1_2_1
      ),
      category: 'PET thermoforms',
      percentage: tableData__4_3.pet_thermoforms__percent__4_3_2_2_1,
    },
    {
      isVisible: convertStringToBoolean(
        tableData__4_3.other_pet_rigids__4_3_1_3_1
      ),
      category: 'Other PET rigids',
      percentage: tableData__4_3.other_pet_rigids__percent__4_3_2_3_1,
    },
    {
      isVisible: convertStringToBoolean(tableData__4_3.hdpe_bottles__4_3_1_4_1),
      category: 'HDPE bottles',
      percentage: tableData__4_3.hdpe_bottles__percent__4_3_2_4_1,
    },
    {
      isVisible: convertStringToBoolean(
        tableData__4_3.hdpe_other_rigids__4_3_1_5_1
      ),
      category: 'HDPE other rigids',
      percentage: tableData__4_3.hdpe_other_rigids__percent__4_3_2_5_1,
    },
    {
      isVisible: convertStringToBoolean(tableData__4_3.pp_bottles__4_3_1_6_1),
      category: 'PP bottles',
      percentage: tableData__4_3.pp_bottles__percent__4_3_2_6_1,
    },
    {
      isVisible: convertStringToBoolean(
        tableData__4_3.pp_other_rigids__4_3_1_7_1
      ),
      category: 'PP other rigids',
      percentage: tableData__4_3.pp_other_rigids__percent__4_3_2_7_1,
    },
    {
      isVisible: convertStringToBoolean(tableData__4_3.pe_tubes__4_3_1_8_1),
      category: 'PE tubes',
      percentage: tableData__4_3.pe_tubes__percent__4_3_2_8_1,
    },
    {
      isVisible: convertStringToBoolean(tableData__4_3.ps_rigids__4_3_1_9_1),
      category: 'PS rigids',
      percentage: tableData__4_3.ps_rigids__percent__4_3_2_9_1,
    },
    {
      isVisible: convertStringToBoolean(tableData__4_3.eps_rigids__4_3_1_10_1),
      category: 'EPS rigids',
      percentage: tableData__4_3.eps_rigids__percent__4_3_2_10_1,
    },
    {
      isVisible: convertStringToBoolean(tableData__4_3.pvc_rigids__4_3_1_11_1),
      category: 'PVC rigids',
      percentage: tableData__4_3.pvc_rigids__percent__4_3_2_11_1,
    },
  ].filter(element => element.isVisible === true);

  const flexibleGroupData = [
    {
      isVisible: convertStringToBoolean(
        tableData__4_3._a4_monomaterials_pe_in_b2b_context__4_3_1_12_1
      ),
      category: '>A4 mono-materials PE in B2B context',
      percentage:
        tableData__4_3._a4_monomaterials_pe_in_b2b_context__percent__4_3_2_12_1,
    },
    {
      isVisible: convertStringToBoolean(
        tableData__4_3._a4_monomaterials_pe_in_b2c_context__4_3_1_13_1
      ),
      category: '>A4 mono-materials PE in B2C context',
      percentage:
        tableData__4_3._a4_monomaterials_pe_in_b2c_context__percent__4_3_2_13_1,
    },
    {
      isVisible: convertStringToBoolean(
        tableData__4_3.other__a4_flexibles__4_3_1_14_1
      ),
      category: 'Other >A4 flexibles',
      percentage: tableData__4_3.other__a4_flexibles__percent__4_3_2_14_1,
    },
    {
      isVisible: convertStringToBoolean(
        tableData__4_3._a4_flexibles_pe__4_3_1_15_1
      ),
      category: '<A4 flexibles PE',
      percentage: tableData__4_3._a4_flexibles_pe__percent__4_3_2_15_1,
    },
    {
      isVisible: convertStringToBoolean(
        tableData__4_3._a4_flexibles_pp__4_3_1_16_1
      ),
      category: '<A4 flexibles PP',
      percentage: tableData__4_3._a4_flexibles_pp__percent__4_3_2_16_1,
    },
    {
      isVisible: convertStringToBoolean(
        tableData__4_3._a4_multimaterial_flexibles__4_3_1_17_1
      ),
      category: '<A4 multi-material flexibles',
      percentage:
        tableData__4_3._a4_multimaterial_flexibles__percent__4_3_2_17_1,
    },
    {
      isVisible: convertStringToBoolean(
        tableData__4_3.other__a4_monomaterial_flexibles__4_3_1_18_1
      ),
      category: 'Other <A4 mono-material flexibles',
      percentage:
        tableData__4_3.other__a4_monomaterial_flexibles__percent__4_3_2_18_1,
    },
  ].filter(element => element.isVisible === true);

  const otherGroupData = [
    {
      isVisible: convertStringToBoolean(tableData__4_3.other__4_3_1_19_1),
      category: tableData__4_3.other__description__4_3_1_19_text,
      percentage: tableData__4_3.other__percent__4_3_2_19_1,
    },
  ].filter(element => element.isVisible === true);

  const linkData = data.categoryLinks.nodes;
  const organisationListingUrl = `${siteVars.GLOBAL_COMMITMENT_BASE_PATH}/${siteVars.GLOBAL_COMMITMENT_LISTING_PAGE_SLUG}`;
  const title = pageData.organisation_name__b_1;
  const category = pageData.category__b_0;
  const metaTitle = pageData.meta_title__b_6_1;
  const metaDescription = pageData.meta_description__b_6_2;

  // generate proper links for companies dropdown
  const gcDropdownLinks = generateGcDropdownLinks(linkData, category, title);

  // Tags data handling
  const transformedTags = transformTags(['Plastics']);

  const metaData = createGcMetaData(title, metaTitle, metaDescription);

  const gtmDataLayerVars = {
    pageId: pageContext.id,
    event: 'Page View',
    pageType: `standard page${
      pageContext.pageType ? ` - ${pageContext.pageType}` : null
    }`,
    topics: transformedTags,
  };

  return (
    <Layout
      footerData={footerData}
      translate={false}
      className={className}
      metaData={metaData}
      gtmDataLayerVars={gtmDataLayerVars}
      globals={pageContext?.globals ? pageContext.globals : null}
    >
      {' '}
      <div className="wrapper">
        <SectionLayout>
          <ProfileContainer>
            <SubHeader
              title={title}
              category={category}
              firstYearReport={pageData.first_year__rep}
              previousYearSlug={pageData.previous_report_url__b_5}
              organisationListingUrl={organisationListingUrl}
              linkData={gcDropdownLinks}
            />
            <SecondaryNav tabs={companySecondaryNavData} />
            <div>
              <div id="key-metrics" className="link-destination" />

              <MetricsFramework
                title="Key Metrics"
                footnote={pageData.not_aligned_caption__dev_note}
                isVisible={convertStringToBoolean(
                  pageData.key_metrics_block__display__key_metrics_display
                )}
              >
                <MetricsColumn>
                  {pageData.weight_new_plastic_packaging__alternative__4_2 ||
                  pageData.weight_new_plastic_packaging__metrics_tonnes___4_1_1 ? (
                    <MetricsRow label="Plastic packaging weight">
                      {pageData.weight_new_plastic_packaging__alternative__4_2
                        ? pageData.weight_new_plastic_packaging__alternative__4_2
                        : `${formatNumberWithCommas(
                            pageData.weight_new_plastic_packaging__metrics_tonnes___4_1_1
                          )} metric tonnes`}
                    </MetricsRow>
                  ) : null}
                  {pageData.reduction_target__full_sentence__rt_full ? (
                    <MetricsRow label="Total and/or virgin plastic packaging reduction target">
                      {pageData.reduction_target__full_sentence__rt_full}
                    </MetricsRow>
                  ) : null}
                  {pageData._2020_reusable_plastic_packaging__percent___4_5_13 &&
                  pageData._2019_reusable_plastic_packaging__percent___3_2_12 ? (
                    <MetricsRow label="Reusable plastic packaging">
                      <PercentageChange
                        currentValue={convertStringToFloat(
                          pageData._2020_reusable_plastic_packaging__percent___4_5_13
                        )}
                        previousValue={convertStringToFloat(
                          pageData._2019_reusable_plastic_packaging__percent___3_2_12
                        )}
                      />{' '}
                      from prior year
                    </MetricsRow>
                  ) : (
                    <MetricsRow label="Reusable plastic packaging">
                      {pageData.reusable_packaging__alternative__4_5_13_excep ? (
                        pageData.reusable_packaging__alternative__4_5_13_excep
                      ) : (
                        <PercentageChange
                          currentValue={convertStringToFloat(
                            pageData._2020_reusable_plastic_packaging__percent___4_5_13
                          )}
                          previousValue={
                            pageData._2019_reusable_plastic_packaging__percent___3_2_12
                              ? convertStringToFloat(
                                  pageData._2019_reusable_plastic_packaging__percent___3_2_12
                                )
                              : undefined
                          }
                          isFirstYear={convertStringToBoolean(
                            pageData.first_year__rep
                          )}
                        />
                      )}
                    </MetricsRow>
                  )}
                </MetricsColumn>
                <MetricsColumn>
                  {convertStringToBoolean(
                    pageData.visual_1__rrc___display__v1_display
                  ) ? (
                    <VerticalBarGraph
                      type="percentage"
                      title="Reusable, recyclable or compostable plastic packaging"
                      pill={pageData.deviation__dev}
                      bars={buildBarData([
                        {
                          date: '2018',
                          value:
                            pageData.percent_recyclable_reusable_and_compostable_2018___3_2_16,
                        },
                        {
                          date: '2019',
                          value:
                            pageData._2019_reusable_recyclable_or_compostable__percent___3_2_17,
                        },
                        {
                          date: '2020',
                          value:
                            pageData._2020_reusable_recyclable_or_compostable__percent___4_5_1,
                          colour: '#3780c1',
                        },
                      ])}
                      target={{
                        value:
                          pageData.target_reusable_recyclable_and_or_compostable__rrc_target,
                        label: `${pageData.target_reusable_recyclable_and_or_compostable__rrc_target}%`,
                      }}
                    />
                  ) : (
                    <VerticalBarGraph
                      title="Reusable, recyclable or compostable plastic packaging"
                      altText={
                        pageData.visual_1__rrc___alternative_text__v1_alt
                      }
                    />
                  )}
                </MetricsColumn>
                <MetricsColumn>
                  {convertStringToBoolean(
                    pageData.visual_2__pcr___display__v2_display
                  ) ? (
                    <VerticalBarGraph
                      type="percentage"
                      title="Post-consumer recycled content"
                      bars={buildBarData([
                        {
                          date: '2018',
                          value:
                            pageData.percent_post_consumer_recycled_content_2018__3_2_4,
                        },
                        {
                          date: '2019',
                          value:
                            pageData._2019_postconsumer_recycled_content__percent___3_2_5,
                        },
                        {
                          date: '2020',
                          value:
                            pageData._2020_postconsumer_recycled_content__percent___4_4_11,
                          colour: '#3780c1',
                        },
                      ])}
                      target={{
                        value:
                          pageData.target_postconsumer_recycled_content__percent___pcr_target,
                        label: `${pageData.target_postconsumer_recycled_content__percent___pcr_target}%`,
                      }}
                    />
                  ) : (
                    <VerticalBarGraph
                      title="Post-consumer recycled content"
                      altText={
                        pageData.visual_2__pcr___alternative_text__v2_alt
                      }
                    />
                  )}
                </MetricsColumn>
              </MetricsFramework>
            </div>
            <div id="company-details" className="link-destination" />

            <HorizontalTable
              title="Company Details"
              isVisible={convertStringToBoolean(
                pageData.company_details_block__display__company_details_display
              )}
            >
              {pageData.description__1_1 ? (
                <TableRow label="Description">
                  <FormattedText inputText={pageData.description__1_1} />
                </TableRow>
              ) : null}
              {pageData.website__1_2 ? (
                <TableRow label="Website">
                  <PAtom>
                    <FlexibleLinkAtom url={pageData.website__1_2} />
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.revenue_band__usd___1_4_2_1 ? (
                <TableRow label="Revenue band (USD)">
                  <PAtom>{pageData.revenue_band__usd___1_4_2_1}</PAtom>
                </TableRow>
              ) : null}
              {pageData.headquarters_location__1_5 ? (
                <TableRow label="Headquarters location">
                  <PAtom>{pageData.headquarters_location__1_5}</PAtom>
                </TableRow>
              ) : null}
              {pageData.regions_active__1_6 ? (
                <TableRow label="Regions active">
                  <FormattedText inputText={pageData.regions_active__1_6} />
                </TableRow>
              ) : null}
              {pageData.products_sold_in_plastic_packaging__5_1 ? (
                <TableRow label="Products sold in plastic packaging">
                  <PAtom>
                    {pageData.products_sold_in_plastic_packaging__5_1}
                  </PAtom>
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="reporting-details" className="link-destination" />

            <HorizontalTable
              title="Reporting details"
              isVisible={convertStringToBoolean(
                pageData.reporting_details_block__display__reporting_details_display
              )}
            >
              {pageData.data_verification_assurance_status__2_2 ? (
                <TableRow label="Data verification/assurance status">
                  <PAtom>
                    {pageData.data_verification_assurance_status__2_2}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.scope_of_reported_data__2_5 ? (
                <TableRow label="Scope of reported data">
                  <PAtom>{pageData.scope_of_reported_data__2_5}</PAtom>
                </TableRow>
              ) : null}
              {pageData._12_months_time_frame_for_quantitative_data__2_1 ? (
                <TableRow label="12-month quantitative data time frame">
                  <PAtom>
                    {pageData._12_months_time_frame_for_quantitative_data__2_1}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.joined_the_global_commitment__join_date ? (
                <TableRow label="Joined the Global Commitment">
                  <PAtom>
                    {pageData.joined_the_global_commitment__join_date}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.ellen_macarthur_foundations_plastics_pact_network_membership__as_of_18_05_2020___2_7 ? (
                <TableRow label="Ellen MacArthur Foundation's Plastics Pact Network membership (as of 18/05/2021)">
                  <PAtom>
                    {
                      pageData.ellen_macarthur_foundations_plastics_pact_network_membership__as_of_18_05_2020___2_7
                    }
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.webpage_or_report_addressing_progress__1_3 ? (
                <TableRow label="Webpage or report addressing progress">
                  <PAtom>
                    {pageData.webpage_or_report_addressing_progress__1_3}
                  </PAtom>
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="portfolio" className="link-destination" />

            <HorizontalTable
              title="Plastic packaging portfolio details"
              isVisible={convertStringToBoolean(
                pageData.plastic_packaging_portfolio_details_block__display__portfolio_display
              )}
            >
              <PAtom>
                See endnotes for more information on quantitative data
              </PAtom>

              {pageData.weight_new_plastic_packaging__metrics_tonnes___4_1_1 ? (
                <TableRow label="Plastic packaging weight (new packaging)">
                  <BoldPAtom>
                    {formatNumberWithCommas(
                      pageData.weight_new_plastic_packaging__metrics_tonnes___4_1_1
                    )}{' '}
                    metric tonnes
                  </BoldPAtom>
                </TableRow>
              ) : pageData.weight_new_plastic_packaging__alternative__4_2 ? (
                <TableRow label="Plastic packaging weight (new packaging)">
                  <PAtom>
                    {pageData.weight_new_plastic_packaging__alternative__4_2}
                  </PAtom>
                </TableRow>
              ) : null}

              {pageData.reused_plastic_packaging_weight__metric_tonnes___4_1_2 ? (
                <TableRow label="Reused plastic packaging weight">
                  <PAtom>
                    {formatNumberWithCommas(
                      pageData.reused_plastic_packaging_weight__metric_tonnes___4_1_2
                    )}{' '}
                    metric tonnes
                  </PAtom>
                </TableRow>
              ) : null}

              <TableRow label="Plastic packaging categories in portfolio">
                {convertStringToBoolean(
                  pageData.plastic_packaging_categories_in_portfolio__display_tables__4_3_tables_display
                ) ? (
                  <GroupedTablesContainer>
                    <GroupedTable tableHeadData={tableHeadGrouped} />
                    <GroupedTable
                      tableHeadData={tableHeadGroupedNoLabels}
                      tableData={rigidsGroupData}
                      firstRowLabel="Rigids"
                      isVisible={convertStringToBoolean(
                        tableData__4_3.rigids_table__display__4_3_rigids_display
                      )}
                    />
                    <GroupedTable
                      tableHeadData={tableHeadGroupedNoLabels}
                      tableData={flexibleGroupData}
                      firstRowLabel="Flexible"
                      isVisible={convertStringToBoolean(
                        tableData__4_3.flexibles_table__display__4_3_flexibles_display
                      )}
                    />
                    <GroupedTable
                      tableHeadData={tableHeadGroupedNoLabels}
                      tableData={otherGroupData}
                      firstRowLabel="Other"
                      isVisible={convertStringToBoolean(
                        tableData__4_3.other_table__display__4_3_other_display
                      )}
                    />
                  </GroupedTablesContainer>
                ) : (
                  <PAtom>
                    {
                      pageData.plastic_packaging_categories_in_portfolio__alternative_text__4_3_tables_alt
                    }
                  </PAtom>
                )}
              </TableRow>
              <TableRow label="Plastic packaging design assessment">
                {convertStringToBoolean(
                  pageData.bar_chart__plastic_packaging_design__display__4_5_bar_chart_display
                ) ? (
                  <VisualisationBlock
                    type="bar"
                    data={[
                      {
                        label: 'Reusable, recyclable and/or compostable',
                        value:
                          pageData._2020_reusable_recyclable_or_compostable__percent___4_5_1,
                      },
                      {
                        label: 'Reusable',
                        value:
                          pageData._2020_reusable_plastic_packaging__percent___4_5_13,
                      },
                      {
                        label: 'Recyclable',
                        value: pageData._2020_recyclable__4_5_24,
                      },
                      {
                        label: 'Compostable',
                        value: pageData._2020_compostable__4_5_25,
                      },
                    ]}
                  />
                ) : (
                  <PAtom>
                    {
                      pageData.bar_chart__plastic_packaging_design__alternative_text__4_5_bar_chart_alt
                    }
                  </PAtom>
                )}
              </TableRow>
              <TableRow label="Material sourcing">
                {convertStringToBoolean(
                  pageData.pie_chart__material_sourcing__display__4_4_pie_chart_display
                ) ? (
                  <VisualisationBlock
                    type="pie"
                    data={[
                      {
                        label: 'Virgin fossil-based content',
                        value:
                          pageData._2020_fossilbased_virgin_content__4_4_15,
                        colour: vars.darkGrey,
                      },
                      {
                        label: 'Post-consumer recycled content',
                        value:
                          pageData._2020_postconsumer_recycled_content__percent___4_4_11,
                        colour: vars.plasticsBlue,
                      },
                      {
                        label: 'Pre-consumer recycled content',
                        value:
                          pageData._2020_preconsumer_recycled_content__4_4_12,
                        colour: vars.borderGrey,
                      },
                      {
                        label: 'Virgin renewable content',
                        value: pageData._2020_virgin_renewable_content__4_4_14,
                        colour: vars.lightGrey,
                      },
                    ]}
                  />
                ) : (
                  <PAtom>
                    {
                      pageData.pie_chart__material_sourcing__alternative_text__4_4_pie_chart_alt
                    }
                  </PAtom>
                )}
              </TableRow>

              {pageData.link_to_other_published_data__4_15 ? (
                <TableRow label="Link to other published data">
                  <PAtom>{pageData.link_to_other_published_data__4_15}</PAtom>
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="commitments" className="link-destination" />

            <SubHeaderAtom displaySeparator={true}>Commitments</SubHeaderAtom>

            <HorizontalTable
              title="Plastic and/or virgin plastic packaging reduction target"
              isVisible={convertStringToBoolean(
                pageData.reduction_target_block__display__reduction_target_display
              )}
            >
              {pageData.reduction_target__full_sentence__rt_full ? (
                <TableRow label="2025 target">
                  <PAtom>
                    {pageData.reduction_target__full_sentence__rt_full}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.plastic_packaging_weight_baseline__rt_4 ? (
                <TableRow label="Plastic packaging weight baseline">
                  <PAtom>
                    {pageData.plastic_packaging_weight_baseline__rt_4}
                  </PAtom>
                </TableRow>
              ) : null}
              {pageData.additional_information_about_the_target__rt_5 ? (
                <TableRow label="Additional information about the target">
                  <FormattedText
                    inputText={
                      pageData.additional_information_about_the_target__rt_5
                    }
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>

            <HorizontalTable
              title="Eliminating problematic or unnecessary plastic packaging"
              isVisible={convertStringToBoolean(
                pageData.elimination_block__display__elimination_display
              )}
            >
              <SubHeaderAtom>
                Progress made over the reporting period
              </SubHeaderAtom>
              <TableRow
                label="Examples of materials / formats / components eliminated or reduced"
                isFullWidth={
                  convertStringToBoolean(
                    pageData.elimination_table__display__6_elimin_display
                  )
                    ? true
                    : false
                }
              >
                {convertStringToBoolean(
                  pageData.elimination_table__display__6_elimin_display
                ) ? (
                  <MatrixTable tableHeadData={tableHeadElimination}>
                    {tableData__6_1.material_1__name__6_1_4_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_1.material_1__name__6_1_4_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_1__percent_eliminated__6_1_3_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_1__weight_eliminated__6_1_1_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_1__quantity_eliminated__6_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_1__means_of_elimination__6_1_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_1__other_details__6_1_5_1_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_1.material_2__name__6_1_4_2 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_1.material_2__name__6_1_4_2}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_2__percent_eliminated__6_1_3_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_2__weight_eliminated__6_1_1_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_2__quantity_eliminated__6_1_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_2__means_of_elimination__6_1_2_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_2__other_details__6_1_5_2_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_1.material_3__name__6_1_4_3 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_1.material_3__name__6_1_4_3}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_3__percent_eliminated__6_1_3_3_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_3__weight_eliminated__6_1_1_3_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_3__quantity_eliminated__6_1_3
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_3__means_of_elimination__6_1_2_3
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_3__other_details__6_1_5_3_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_1.material_4__name__6_1_4_4 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_1.material_4__name__6_1_4_4}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_4__percent_eliminated__6_1_3_4_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_4__weight_eliminated__6_1_1_4_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_4__quantity_eliminated__6_1_4
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_4__means_of_elimination__6_1_2_4
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_4__other_details__6_1_5_4_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_1.material_5__name__6_1_4_5 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_1.material_5__name__6_1_4_5}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_5__percent_eliminated__6_1_3_5_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_5__weight_eliminated__6_1_1_5_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_5__quantity_eliminated__6_1_5
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_5__means_of_elimination__6_1_2_5
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_5__other_details__6_1_5_5_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_1.material_6__name__6_1_4_6 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_1.material_6__name__6_1_4_6}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_6__percent_eliminated__6_1_3_6_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_6__weight_eliminated__6_1_1_6_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_6__quantity_eliminated__6_1_6
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_6__means_of_elimination__6_1_2_6
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_6__other_details__6_1_5_6_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_1.material_7__name__6_1_4_7 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_1.material_7__name__6_1_4_7}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_7__percent_eliminated__6_1_3_7_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_7__weight_eliminated__6_1_1_7_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_7__quantity_eliminated__6_1_7
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_7__means_of_elimination__6_1_2_7
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_7__other_details__6_1_5_7_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_1.material_8__name__6_1_4_8 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_1.material_8__name__6_1_4_8}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_8__percent_eliminated__6_1_3_8_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_8__weight_eliminated__6_1_1_8_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_8__quantity_eliminated__6_1_8
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_8__means_of_elimination__6_1_2_8
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_8__other_details__6_1_5_8_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_1.material_9__name__6_1_4_9 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_1.material_9__name__6_1_4_9}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_9__percent_eliminated__6_1_3_9_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_9__weight_eliminated__6_1_1_9_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_9__quantity_eliminated__6_1_9
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_9__means_of_elimination__6_1_2_9
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_9__other_details__6_1_5_9_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_1.material_10__name__6_1_4_10 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_1.material_10__name__6_1_4_10}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_10__percent_eliminated__6_1_3_10_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_10__weight_eliminated__6_1_1_10_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_10__quantity_eliminated__6_1_10
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_10__means_of_elimination__6_1_2_10
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_1.material_10__other_details__6_1_5_10_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_2.format_1__name__6_2_4_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_2.format_1__name__6_2_4_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_1__percent_eliminated__6_2_3_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_1__weight_eliminated__6_2_1_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_1__quantity_eliminated__6_2_1_1_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_1__means_of_elimination__6_2_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_1__other_details__6_2_5_1_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_2.format_2__name__6_2_4_2 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_2.format_2__name__6_2_4_2}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_2__percent_eliminated__6_2_3_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_2__weight_eliminated__6_2_1_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_2__quantity_eliminated__6_2_1_2_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_2__means_of_elimination__6_2_2_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_2__other_details__6_2_5_2_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_2.format_3__name__6_2_4_3 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_2.format_3__name__6_2_4_3}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_3__percent_eliminated__6_2_3_3_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_3__weight_eliminated__6_2_1_3_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_3__quantity_eliminated__6_2_1_3_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_3__means_of_elimination__6_2_2_3
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_3__other_details__6_2_5_3_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_2.format_4__name__6_2_4_4 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_2.format_4__name__6_2_4_4}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_4__percent_eliminated__6_2_3_4_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_4__weight_eliminated__6_2_1_4_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_4__quantity_eliminated__6_2_1_4_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_4__means_of_elimination__6_2_2_4
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_4__other_details__6_2_5_4_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_2.format_5__name__6_2_4_5 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_2.format_5__name__6_2_4_5}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_5__percent_eliminated__6_2_3_5_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_5__weight_eliminated__6_2_1_5_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_5__quantity_eliminated__6_2_1_5_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_5__means_of_elimination__6_2_2_5
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_5__other_details__6_2_5_5_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_2.format_6__name__6_2_4_6 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_2.format_6__name__6_2_4_6}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_6__percent_eliminated__6_2_3_6_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_6__weight_eliminated__6_2_1_6_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_6__quantity_eliminated__6_2_1_6_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_6__means_of_elimination__6_2_2_6
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_6__other_details__6_2_5_6_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_2.format_7__name__6_2_4_7 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_2.format_7__name__6_2_4_7}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_7__percent_eliminated__6_2_3_7_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_7__weight_eliminated__6_2_1_7_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_7__quantity_eliminated__6_2_1_7_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_7__means_of_elimination__6_2_2_7
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_7__other_details__6_2_5_7_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_2.format_8__name__6_2_4_8 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_2.format_8__name__6_2_4_8}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_8__percent_eliminated__6_2_3_8_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_8__weight_eliminated__6_2_1_8_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_8__quantity_eliminated__6_2_1_8_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_8__means_of_elimination__6_2_2_8
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_8__other_details__6_2_5_8_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_2.format_9__name__6_2_4_9 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_2.format_9__name__6_2_4_9}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_9__percent_eliminated__6_2_3_9_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_9__weight_eliminated__6_2_1_9_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_9__quantity_eliminated__6_2_1_9_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_9__means_of_elimination__6_2_2_9
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_9__other_details__6_2_5_9_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_2.format_10__name__6_2_4_10 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_2.format_10__name__6_2_4_10}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_10__percent_eliminated__6_2_3_10_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_10__weight_eliminated__6_2_1_10_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_10__quantity_eliminated__6_2_1_10_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_10__means_of_elimination__6_2_2_10
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_2.format_10__other_details__6_2_5_10_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}

                    {tableData__6_3.component_1__name__6_3_4_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_3.component_1__name__6_3_4_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_1__percent_eliminated__6_3_3_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_1__weight_eliminated__6_3_1_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_1__quantity_eliminated__6_3_1_1_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_1__means_of_elimination__6_3_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_1__other_details__6_3_5_1_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_3.component_2__name__6_3_4_2 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_3.component_2__name__6_3_4_2}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_2__percent_eliminated__6_3_3_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_2__weight_eliminated__6_3_1_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_2__quantity_eliminated__6_3_1_2_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_2__means_of_elimination__6_3_2_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_2__other_details__6_3_5_2_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_3.component_3__name__6_3_4_3 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_3.component_3__name__6_3_4_3}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_3__percent_eliminated__6_3_3_3_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_3__weight_eliminated__6_3_1_3_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_3__quantity_eliminated__6_3_1_3_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_3__means_of_elimination__6_3_2_3
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_3__other_details__6_3_5_3_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_3.component_4__name__6_3_4_4 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_3.component_4__name__6_3_4_4}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_4__percent_eliminated__6_3_3_4_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_4__weight_eliminated__6_3_1_4_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_4__quantity_eliminated__6_3_1_4_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_4__means_of_elimination__6_3_2_4
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_4__other_details__6_3_5_4_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_3.component_5__name__6_3_4_5 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_3.component_5__name__6_3_4_5}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_5__percent_eliminated__6_3_3_5_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_5__weight_eliminated__6_3_1_5_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_5__quantity_eliminated__6_3_1_5_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_5__means_of_elimination__6_3_2_5
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_5__other_details__6_3_5_5_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_3.component_6__name__6_3_4_6 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_3.component_6__name__6_3_4_6}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_6__percent_eliminated__6_3_3_6_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_6__weight_eliminated__6_3_1_6_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_6__quantity_eliminated__6_3_1_6_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_6__means_of_elimination__6_3_2_6
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_6__other_details__6_3_5_6_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_3.component_7__name__6_3_4_7 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_3.component_7__name__6_3_4_7}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_7__percent_eliminated__6_3_3_7_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_7__weight_eliminated__6_3_1_7_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_7__quantity_eliminated__6_3_1_7_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_7__means_of_elimination__6_3_2_7
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_7__other_details__6_3_5_7_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_3.component_8__name__6_3_4_8 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_3.component_8__name__6_3_4_8}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_8__percent_eliminated__6_3_3_8_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_8__weight_eliminated__6_3_1_8_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_8__quantity_eliminated__6_3_1_8_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_8__means_of_elimination__6_3_2_8
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_8__other_details__6_3_5_8_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_3.component_9__name__6_3_4_9 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_3.component_9__name__6_3_4_9}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_9__percent_eliminated__6_3_3_9_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_9__weight_eliminated__6_3_1_9_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_9__quantity_eliminated__6_3_1_9_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_9__means_of_elimination__6_3_2_9
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_9__other_details__6_3_5_9_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_3.component_10__name__6_3_4_10 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_3.component_10__name__6_3_4_10}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_10__percent_eliminated__6_3_3_10_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_10__weight_eliminated__6_3_1_10_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_10__quantity_eliminated__6_3_1_10_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_10__means_of_elimination__6_3_2_10
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_3.component_10__other_details__6_3_5_10_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                  </MatrixTable>
                ) : (
                  <PAtom>
                    {pageData.elimination_table__alternative_text__6_elimin_alt}
                  </PAtom>
                )}
              </TableRow>

              {pageData.activites_and_progress_made_on_elimination__6_4 ? (
                <TableRow label="Activities and progress made on elimination">
                  <FormattedText
                    inputText={
                      pageData.activites_and_progress_made_on_elimination__6_4
                    }
                  />
                </TableRow>
              ) : null}

              <SubHeaderAtom displaySeparator={true}>
                Planned actions by 2025
              </SubHeaderAtom>

              <TableRow
                label="Plastic packaging to be eliminated or reduced"
                isFullWidth={
                  convertStringToBoolean(
                    pageData.to_be_eliminated_table__display__6_6_tbe_display
                  )
                    ? true
                    : false
                }
              >
                {convertStringToBoolean(
                  pageData.to_be_eliminated_table__display__6_6_tbe_display
                ) ? (
                  <MatrixTable tableHeadData={tableHeadPlannedActions}>
                    {tableData__6_6.b2b_rigids__6_6_4_17_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.b2b_rigids__6_6_4_17_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.b2b_rigids__percent_to_be_eliminated__6_6_2_17
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.b2b_rigids__weight_to_be_eliminated__6_6_1_17_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.b2b_rigids__quantity_to_be_eliminated__6_6_1_17_2
                          }
                        />
                        <MatrixTableCell
                          content={tableData__6_6.b2b_rigids__year__6_6_3_17}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.b2b_rigids__other_details__6_6_5_17_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.eps__6_6_4_1_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.eps__6_6_4_1_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.eps__percent_to_be_eliminated__6_6_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.eps__weight_to_be_eliminated__6_6_1_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.eps__quantity_to_be_eliminated__6_6_1_1_2
                          }
                        />
                        <MatrixTableCell
                          content={tableData__6_6.eps__year__6_6_3_1}
                        />
                        <MatrixTableCell
                          content={tableData__6_6.eps__other_details__6_6_5_1_1}
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.film__very_small__e_g__portion_size_sachets__6_6_4_10_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.film__very_small__e_g__portion_size_sachets__6_6_4_10_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film__very_small__e_g__portion_size_sachets__percent_to_be_eliminated__6_6_2_10
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film__very_small__e_g__portion_size_sachets__weight_to_be_eliminated__6_6_1_10_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film__very_small__e_g__portion_size_sachets__quantity_to_be_eliminated__6_6_1_10_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film__very_small__e_g__portion_size_sachets__year__6_6_3_10
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film__very_small__e_g__portion_size_sachets__other_details__6_6_5_10_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.film_b2b__e_g__pallet_wrap__6_6_4_13_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2b__e_g__pallet_wrap__6_6_4_13_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2b__e_g__pallet_wrap__percent_to_be_eliminated__6_6_2_13
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2b__e_g__pallet_wrap__weight_to_be_eliminated__6_6_1_13_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2b__e_g__pallet_wrap__quantity_to_be_eliminated__6_6_1_13_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2b__e_g__pallet_wrap__year__6_6_3_13
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2b__e_g__pallet_wrap__other_details__6_6_5_13_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.film_b2c__monomaterial_clear__6_6_4_11_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__monomaterial_clear__6_6_4_11_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__monomaterial_clear__percent_to_be_eliminated__6_6_2_11
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__monomaterial_clear__weight_to_be_eliminated__6_6_1_11_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__monomaterial_clear__quantity_to_be_eliminated__6_6_1_11_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__monomaterial_clear__year__6_6_3_11
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__monomaterial_clear__other_details__6_6_5_11_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.film_b2c__other__opaque_printed_multimaterial___6_6_4_12_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__other__opaque_printed_multimaterial___6_6_4_12_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__other__opaque_printed_multimaterial___percent_to_be_eliminated__6_6_2_12
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__other__opaque_printed_multimaterial___weight_to_be_eliminated__6_6_1_12_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__other__opaque_printed_multimaterial___quantity_to_be_eliminated__6_6_1_12_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__other__opaque_printed_multimaterial___year__6_6_3_12
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.film_b2c__other__opaque_printed_multimaterial___other_details__6_6_5_12_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.labels_stickers_sleeves__6_6_4_22_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.labels_stickers_sleeves__6_6_4_22_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.labels_stickers_sleeves__percent_to_be_eliminated__6_6_2_22
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.labels_stickers_sleeves__weight_to_be_eliminated__6_6_1_22_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.labels_stickers_sleeves__quantity_to_be_eliminated__6_6_1_22_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.labels_stickers_sleeves__year__6_6_3_22
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.labels_stickers_sleeves__other_details__6_6_5_22_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.lids_closures__6_6_4_23_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.lids_closures__6_6_4_23_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.lids_closures__percent_to_be_eliminated__6_6_2_23
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.lids_closures__weight_to_be_eliminated__6_6_1_23_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.lids_closures__quantity_to_be_eliminated__6_6_1_23_2
                          }
                        />
                        <MatrixTableCell
                          content={tableData__6_6.lids_closures__year__6_6_3_23}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.lids_closures__other_details__6_6_5_23_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.metallised_films__6_6_4_2_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.metallised_films__6_6_4_2_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.metallised_films__percent_to_be_eliminated__6_6_2_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.metallised_films__weight_to_be_eliminated__6_6_1_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.metallised_films__quantity_to_be_eliminated__6_6_1_2_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.metallised_films__year__6_6_3_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.metallised_films__other_details__6_6_5_2_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.multilayer_materials__6_6_4_3_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.multilayer_materials__6_6_4_3_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.multilayer_materials__percent_to_be_eliminated__6_6_2_3
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.multilayer_materials__weight_to_be_eliminated__6_6_1_3_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.multilayer_materials__quantity_to_be_eliminated__6_6_1_3_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.multilayer_materials__year__6_6_3_3
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.multilayer_materials__other_details__6_6_5_3_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.other_1__name__6_6_4_27_text ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.other_1__name__6_6_4_27_text}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_1__percent_to_be_eliminated__6_6_2_27
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_1__weight_to_be_eliminated__6_6_1_27_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_1__quantity_to_be_eliminated__6_6_1_27_2
                          }
                        />
                        <MatrixTableCell
                          content={tableData__6_6.other_1__year__6_6_3_27}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_1__other_details__6_6_5_27_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.other_2__name__6_6_4_28_text ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.other_2__name__6_6_4_28_text}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_2__percent_to_be_eliminated__6_6_2_28
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_2__weight_to_be_eliminated__6_6_1_28_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_2__quantity_to_be_eliminated__6_6_1_28_2
                          }
                        />
                        <MatrixTableCell
                          content={tableData__6_6.other_2__year__6_6_3_28}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_2__other_details__6_6_5_28_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.other_pigment_additive__6_6_4_9_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_pigment_additive__6_6_4_9_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_pigment_additive__percent_to_be_eliminated__6_6_2_9
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_pigment_additive__weight_to_be_eliminated__6_6_1_9_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_pigment_additive__quantity_to_be_eliminated__6_6_1_9_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_pigment_additive__year__6_6_3_9
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.other_pigment_additive__other_details__6_6_5_9_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.petg_in_rigid_plastic_packaging__6_6_4_4_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.petg_in_rigid_plastic_packaging__6_6_4_4_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.petg_in_rigid_plastic_packaging__percent_to_be_eliminated__6_6_2_4
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.petg_in_rigid_plastic_packaging__weight_to_be_eliminated__6_6_1_4_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.petg_in_rigid_plastic_packaging__quantity_to_be_eliminated__6_6_1_4_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.petg_in_rigid_plastic_packaging__year__6_6_3_4
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.petg_in_rigid_plastic_packaging__other_details__6_6_5_4_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.plastic_windows__in_cardboard_boxes___6_6_4_24_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.plastic_windows__in_cardboard_boxes___6_6_4_24_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.plastic_windows__in_cardboard_boxes___percent_to_be_eliminated__6_6_2_24
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.plastic_windows__in_cardboard_boxes___weight_to_be_eliminated__6_6_1_24_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.plastic_windows__in_cardboard_boxes___quantity_to_be_eliminated__6_6_1_24_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.plastic_windows__in_cardboard_boxes___year__6_6_3_24
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.plastic_windows__in_cardboard_boxes___other_details__6_6_5_24_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.ps__6_6_4_5_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.ps__6_6_4_5_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.ps__percent_to_be_eliminated__6_6_2_5
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.ps__weight_to_be_eliminated__6_6_1_5_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.ps__quantity_to_be_eliminated__6_6_1_5_2
                          }
                        />
                        <MatrixTableCell
                          content={tableData__6_6.ps__year__6_6_3_5}
                        />
                        <MatrixTableCell
                          content={tableData__6_6.ps__other_details__6_6_5_5_1}
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.pumps_trigger_sprays__6_6_4_25_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.pumps_trigger_sprays__6_6_4_25_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pumps_trigger_sprays__percent_to_be_eliminated__6_6_2_25
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pumps_trigger_sprays__weight_to_be_eliminated__6_6_1_25_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pumps_trigger_sprays__quantity_to_be_eliminated__6_6_1_25_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pumps_trigger_sprays__year__6_6_3_25
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pumps_trigger_sprays__other_details__6_6_5_25_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.pvc__6_6_4_6_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.pvc__6_6_4_6_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pvc__percent_to_be_eliminated__6_6_2_6
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pvc__weight_to_be_eliminated__6_6_1_6_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pvc__quantity_to_be_eliminated__6_6_1_6_2
                          }
                        />
                        <MatrixTableCell
                          content={tableData__6_6.pvc__year__6_6_3_6}
                        />
                        <MatrixTableCell
                          content={tableData__6_6.pvc__other_details__6_6_5_6_1}
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.pvdc__6_6_4_7_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.pvdc__6_6_4_7_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pvdc__percent_to_be_eliminated__6_6_2_7
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pvdc__weight_to_be_eliminated__6_6_1_7_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pvdc__quantity_to_be_eliminated__6_6_1_7_2
                          }
                        />
                        <MatrixTableCell
                          content={tableData__6_6.pvdc__year__6_6_3_7}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.pvdc__other_details__6_6_5_7_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.rigids_b2c__beverage_bottles__6_6_4_14_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__beverage_bottles__6_6_4_14_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__beverage_bottles__percent_to_be_eliminated__6_6_2_14
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__beverage_bottles__weight_to_be_eliminated__6_6_1_14_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__beverage_bottles__quantity_to_be_eliminated__6_6_1_14_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__beverage_bottles__year__6_6_3_14
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__beverage_bottles__other_details__6_6_5_14_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.rigids_b2c__household_personal_products_bottles__6_6_4_15_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__household_personal_products_bottles__6_6_4_15_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__household_personal_products_bottles__percent_to_be_eliminated__6_6_2_15
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__household_personal_products_bottles__weight_to_be_eliminated__6_6_1_15_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__household_personal_products_bottles__quantity_to_be_eliminated__6_6_1_15_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__household_personal_products_bottles__year__6_6_3_15
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__household_personal_products_bottles__other_details__6_6_5_15_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.rigids_b2c__other__e_g__pots_tubs_and_trays___6_6_4_16_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__other__e_g__pots_tubs_and_trays___6_6_4_16_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__other__e_g__pots_tubs_and_trays___percent_to_be_eliminated__6_6_2_16
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__other__e_g__pots_tubs_and_trays___weight_to_be_eliminated__6_6_1_16_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__other__e_g__pots_tubs_and_trays___quantity_to_be_eliminated__6_6_1_16_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__other__e_g__pots_tubs_and_trays___year__6_6_3_16
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.rigids_b2c__other__e_g__pots_tubs_and_trays___other_details__6_6_5_16_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.singleuse_carrier_bags__6_6_4_18_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_carrier_bags__6_6_4_18_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_carrier_bags__percent_to_be_eliminated__6_6_2_18
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_carrier_bags__weight_to_be_eliminated__6_6_1_18_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_carrier_bags__quantity_to_be_eliminated__6_6_1_18_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_carrier_bags__year__6_6_3_18
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_carrier_bags__other_details__6_6_5_18_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.singleuse_cutlery_serveware__6_6_4_19_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_cutlery_serveware__6_6_4_19_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_cutlery_serveware__percent_to_be_eliminated__6_6_2_19
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_cutlery_serveware__weight_to_be_eliminated__6_6_1_19_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_cutlery_serveware__quantity_to_be_eliminated__6_6_1_19_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_cutlery_serveware__year__6_6_3_19
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_cutlery_serveware__other_details__6_6_5_19_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.singleuse_hangers__for_clothing___6_6_4_20_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_hangers__for_clothing___6_6_4_20_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_hangers__for_clothing___percent_to_be_eliminated__6_6_2_20
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_hangers__for_clothing___weight_to_be_eliminated__6_6_1_20_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_hangers__for_clothing___quantity_to_be_eliminated__6_6_1_20_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_hangers__for_clothing___year__6_6_3_20
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_hangers__for_clothing___other_details__6_6_5_20_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.singleuse_straws__6_6_4_21_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.singleuse_straws__6_6_4_21_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_straws__percent_to_be_eliminated__6_6_2_21
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_straws__weight_to_be_eliminated__6_6_1_21_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_straws__quantity_to_be_eliminated__6_6_1_21_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_straws__year__6_6_3_21
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.singleuse_straws__other_details__6_6_5_21_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.tearoffs__6_6_4_26_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__6_6.tearoffs__6_6_4_26_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.tearoffs__percent_to_be_eliminated__6_6_2_26
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.tearoffs__weight_to_be_eliminated__6_6_1_26_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.tearoffs__quantity_to_be_eliminated__6_6_1_26_2
                          }
                        />
                        <MatrixTableCell
                          content={tableData__6_6.tearoffs__year__6_6_3_26}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.tearoffs__other_details__6_6_5_26_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__6_6.undetectable_carbon_black__6_6_4_8_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__6_6.undetectable_carbon_black__6_6_4_8_1
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.undetectable_carbon_black__percent_to_be_eliminated__6_6_2_8
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.undetectable_carbon_black__weight_to_be_eliminated__6_6_1_8_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.undetectable_carbon_black__quantity_to_be_eliminated__6_6_1_8_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.undetectable_carbon_black__year__6_6_3_8
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__6_6.undetectable_carbon_black__other_details__6_6_5_8_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                  </MatrixTable>
                ) : (
                  <PAtom>
                    {
                      pageData.to_be_eliminated_table__alternative_text__6_6_tbe_alt
                    }
                  </PAtom>
                )}
              </TableRow>
              {pageData.planned_actions_on_elimination__6_7 ? (
                <TableRow label="Planned actions on elimination">
                  <FormattedText
                    inputText={pageData.planned_actions_on_elimination__6_7}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>

            <HorizontalTable
              title="Moving from single-use towards reuse models"
              isVisible={convertStringToBoolean(
                pageData.reuse_block__display__reuse_display
              )}
            >
              <SubHeaderAtom>
                Progress made over the reporting period
              </SubHeaderAtom>

              <TableRow label="Share of plastic packaging that is reusable (2020)">
                {pageData._2020_reusable_plastic_packaging__percent___4_5_13 !==
                '' ? (
                  <>
                    <PercentageChange
                      currentValue={convertStringToFloat(
                        pageData._2020_reusable_plastic_packaging__percent___4_5_13
                      )}
                      previousValue={
                        pageData._2019_reusable_plastic_packaging__percent___3_2_12
                          ? convertStringToFloat(
                              pageData._2019_reusable_plastic_packaging__percent___3_2_12
                            )
                          : undefined
                      }
                    />
                    {pageData._2019_reusable_plastic_packaging__percent___3_2_12
                      ? ' from prior year'
                      : null}
                  </>
                ) : (
                  <PAtom>
                    {pageData.reusable_packaging__alternative__4_5_13_excep}
                  </PAtom>
                )}
              </TableRow>

              {pageData.availability_of_reuse_models_today__7_1 ? (
                <TableRow label="Availability of reuse models today">
                  <FormattedText
                    inputText={pageData.availability_of_reuse_models_today__7_1}
                  />
                </TableRow>
              ) : null}
              <TableRow label="Product applications and reuse model types">
                {convertStringToBoolean(
                  pageData.reuse_table__display__7_5_reuse_display
                ) ? (
                  <MatrixTable tableHeadData={tableHeadReuse}>
                    {tableData__7_5.alcoholic_beverages__7_5_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_5.alcoholic_beverages__7_5_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.alcoholic_beverages__reuse_models_in_place__7_5_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.alcoholic_beverages__market__7_5_2_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.alcoholic_beverages__other_details__7_5_3_1_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___7_5_2 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_5.chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___7_5_2
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___reuse_models_in_place__7_5_1_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___market__7_5_2_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___other_details__7_5_3_2_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.cleaning_products__7_5_3 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_5.cleaning_products__7_5_3}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.cleaning_products__reuse_models_in_place__7_5_1_3
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.cleaning_products__market__7_5_2_3_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.cleaning_products__other_details__7_5_3_3_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.clothing___apparel__7_5_4 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_5.clothing___apparel__7_5_4}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.clothing___apparel__reuse_models_in_place__7_5_1_4
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.clothing___apparel__market__7_5_2_4_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.clothing___apparel__other_details__7_5_3_4_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.cosmetics___personal_care__7_5_5 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_5.cosmetics___personal_care__7_5_5
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.cosmetics___personal_care__reuse_models_in_place__7_5_1_5
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.cosmetics___personal_care__market__7_5_2_5_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.cosmetics___personal_care__other_details__7_5_3_5_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.electronics__7_5_6 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_5.electronics__7_5_6}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.electronics__reuse_models_in_place__7_5_1_6
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.electronics__market__7_5_2_6_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.electronics__other_details__7_5_3_6_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.food___nutrition__7_5_7 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_5.food___nutrition__7_5_7}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.food___nutrition__reuse_models_in_place__7_5_1_7
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.food___nutrition__market__7_5_2_7_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.food___nutrition__other_details__7_5_3_7_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.furniture_furnishings___household_items__7_5_8 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_5.furniture_furnishings___household_items__7_5_8
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.furniture_furnishings___household_items__reuse_models_in_place__7_5_1_8
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.furniture_furnishings___household_items__market__7_5_2_8_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.furniture_furnishings___household_items__other_details__7_5_3_8_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.home_improvement___gardening__7_5_9 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_5.home_improvement___gardening__7_5_9
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.home_improvement___gardening__reuse_models_in_place__7_5_1_9
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.home_improvement___gardening__market__7_5_2_9_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.home_improvement___gardening__other_details__7_5_3_9_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.nonalcoholic_beverages__7_5_10 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_5.nonalcoholic_beverages__7_5_10
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.nonalcoholic_beverages__reuse_models_in_place__7_5_1_10
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.nonalcoholic_beverages__market__7_5_2_10_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.nonalcoholic_beverages__other_details__7_5_3_10_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.other__name__7_5_1_16_text ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_5.other__name__7_5_1_16_text}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.other__reuse_models_in_place__7_5_1_16
                          }
                        />
                        <MatrixTableCell
                          content={tableData__7_5.other__market__7_5_2_16_1}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.other__other_details__7_5_3_16_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.pet___animal_supplies__7_5_11 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_5.pet___animal_supplies__7_5_11}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.pet___animal_supplies__reuse_models_in_place__7_5_1_11
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.pet___animal_supplies__market__7_5_2_11_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.pet___animal_supplies__other_details__7_5_3_11_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.pharmaceutical___medical_goods__7_5_12 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_5.pharmaceutical___medical_goods__7_5_12
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.pharmaceutical___medical_goods__reuse_models_in_place__7_5_1_12
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.pharmaceutical___medical_goods__market__7_5_2_12_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.pharmaceutical___medical_goods__other_details__7_5_3_12_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.productagnostic_packaging__e_g__shrink_wrap_pallets___7_5_13 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_5.productagnostic_packaging__e_g__shrink_wrap_pallets___7_5_13
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.productagnostic_packaging__e_g__shrink_wrap_pallets___reuse_models_in_place__7_5_1_13
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.productagnostic_packaging__e_g__shrink_wrap_pallets___market__7_5_2_13_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.productagnostic_packaging__e_g__shrink_wrap_pallets___other_details__7_5_3_13_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.tobacco__7_5_14 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_5.tobacco__7_5_14}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.tobacco__reuse_models_in_place__7_5_1_14
                          }
                        />
                        <MatrixTableCell
                          content={tableData__7_5.tobacco__market__7_5_2_14_1}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.tobacco__other_details__7_5_3_14_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_5.toys__7_5_15 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_5.toys__7_5_15}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.toys__reuse_models_in_place__7_5_1_15
                          }
                        />
                        <MatrixTableCell
                          content={tableData__7_5.toys__market__7_5_2_15_1}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_5.toys__other_details__7_5_3_15_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                  </MatrixTable>
                ) : (
                  <PAtom>
                    {pageData.reuse_table__alternative_text__7_5_reuse_alt}
                  </PAtom>
                )}
              </TableRow>

              {pageData.pilots_launched__7_2 ? (
                <TableRow label="Pilots launched">
                  <PAtom>{pageData.pilots_launched__7_2}</PAtom>
                </TableRow>
              ) : null}

              {pageData.activities_and_progress_made_over_the_reporting_period_on_reuse__7_6 ? (
                <TableRow label="Activities and progress made on reuse">
                  <FormattedText
                    inputText={
                      pageData.activities_and_progress_made_over_the_reporting_period_on_reuse__7_6
                    }
                  />
                </TableRow>
              ) : null}

              <SubHeaderAtom displaySeparator={true}>
                Planned action by 2025
              </SubHeaderAtom>

              <TableRow label="Product applications and reuse model types to be introduced or expanded">
                {convertStringToBoolean(
                  pageData.reuse_to_be_introduced_table__display__7_10_r_plan_display
                ) ? (
                  <MatrixTable tableHeadData={tableHeadreusePlanneActions}>
                    {tableData__7_10.alcoholic_beverages__7_10_1 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_10.alcoholic_beverages__7_10_1}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.alcoholic_beverages__market_in_which_it_will_be_introduced__7_10_2_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.alcoholic_beverages__reuse_models_to_be_introduce__7_10_1_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.alcoholic_beverages__other_details__7_10_3_1_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___7_10_2 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_10.chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___7_10_2
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___reuse_models_to_be_introduce__7_10_1_2
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___market_in_which_it_will_be_introduced__7_10_2_2_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.chemicals_in_a_b2b_context__for_example_for_industrial_or_agricultural_use___other_details__7_10_3_2_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.cleaning_products__7_10_3 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_10.cleaning_products__7_10_3}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.cleaning_products__reuse_models_to_be_introduce__7_10_1_3
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.cleaning_products__market_in_which_it_will_be_introduced__7_10_2_3_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.cleaning_products__other_details__7_10_3_3_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.clothing___apparel__7_10_4 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_10.clothing___apparel__7_10_4}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.clothing___apparel__reuse_models_to_be_introduce__7_10_1_4
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.clothing___apparel__market_in_which_it_will_be_introduced__7_10_2_4_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.clothing___apparel__other_details__7_10_3_4_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.cosmetics___personal_care__7_10_5 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_10.cosmetics___personal_care__7_10_5
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.cosmetics___personal_care__reuse_models_to_be_introduce__7_10_1_5
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.cosmetics___personal_care__market_in_which_it_will_be_introduced__7_10_2_5_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.cosmetics___personal_care__other_details__7_10_3_5_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.electronics__7_10_6 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_10.electronics__7_10_6}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.electronics__reuse_models_to_be_introduce__7_10_1_6
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.electronics__market_in_which_it_will_be_introduced__7_10_2_6_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.electronics__other_details__7_10_3_6_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.food___nutrition__7_10_7 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_10.food___nutrition__7_10_7}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.food___nutrition__reuse_models_to_be_introduce__7_10_1_7
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.food___nutrition__market_in_which_it_will_be_introduced__7_10_2_7_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.food___nutrition__other_details__7_10_3_7_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.furniture_furnishings___household_items__7_10_8 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_10.furniture_furnishings___household_items__7_10_8
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.furniture_furnishings___household_items__reuse_models_to_be_introduce__7_10_1_8
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.furniture_furnishings___household_items__market_in_which_it_will_be_introduced__7_10_2_8_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.furniture_furnishings___household_items__other_details__7_10_3_8_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.home_improvement___gardening__7_10_9 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_10.home_improvement___gardening__7_10_9
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.home_improvement___gardening__reuse_models_to_be_introduce__7_10_1_9
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.home_improvement___gardening__market_in_which_it_will_be_introduced__7_10_2_9_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.home_improvement___gardening__other_details__7_10_3_9_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.nonalcoholic_beverages__7_10_10 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_10.nonalcoholic_beverages__7_10_10
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.nonalcoholic_beverages__reuse_models_to_be_introduce__7_10_1_10
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.nonalcoholic_beverages__market_in_which_it_will_be_introduced__7_10_2_10_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.nonalcoholic_beverages__other_details__7_10_3_10_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.other__7_10_16 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_10.other__name__7_10_1_16_text}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.other__reuse_models_to_be_introduce__7_10_1_16
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.other__market_in_which_it_will_be_introduced__7_10_2_16_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.other__other_details__7_10_3_16_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.pet___animal_supplies__7_10_11 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_10.pet___animal_supplies__7_10_11
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.pet___animal_supplies__reuse_models_to_be_introduce__7_10_1_11
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.pet___animal_supplies__market_in_which_it_will_be_introduced__7_10_2_11_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.pet___animal_supplies__other_details__7_10_3_11_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.pharmaceutical___medical_goods__7_10_12 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_10.pharmaceutical___medical_goods__7_10_12
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.pharmaceutical___medical_goods__reuse_models_to_be_introduce__7_10_1_12
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.pharmaceutical___medical_goods__market_in_which_it_will_be_introduced__7_10_2_12_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.pharmaceutical___medical_goods__other_details__7_10_3_12_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.productagnostic_packaging__e_g__shrink_wrap_pallets___7_10_13 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={
                            tableData__7_10.productagnostic_packaging__e_g__shrink_wrap_pallets___7_10_13
                          }
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.productagnostic_packaging__e_g__shrink_wrap_pallets___reuse_models_to_be_introduce__7_10_1_13
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.productagnostic_packaging__e_g__shrink_wrap_pallets___market_in_which_it_will_be_introduced__7_10_2_13_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.productagnostic_packaging__e_g__shrink_wrap_pallets___other_details__7_10_3_13_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.tobacco__7_10_14 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_10.tobacco__7_10_14}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.tobacco__reuse_models_to_be_introduce__7_10_1_14
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.tobacco__market_in_which_it_will_be_introduced__7_10_2_14_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.tobacco__other_details__7_10_3_14_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                    {tableData__7_10.toys__7_10_15 ? (
                      <MatrixTableRow>
                        <MatrixTableCell
                          content={tableData__7_10.toys__7_10_15}
                          isRowlLabel={true}
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.toys__reuse_models_to_be_introduce__7_10_1_15
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.toys__market_in_which_it_will_be_introduced__7_10_2_15_1
                          }
                        />
                        <MatrixTableCell
                          content={
                            tableData__7_10.toys__other_details__7_10_3_15_1
                          }
                        />
                      </MatrixTableRow>
                    ) : null}
                  </MatrixTable>
                ) : (
                  <PAtom>
                    {
                      pageData.reuse_to_be_introduced_table__alternative_text__7_10_r_plan_alt
                    }
                  </PAtom>
                )}
              </TableRow>

              {pageData.other_2025_reuse_goals__7_11 ? (
                <TableRow label="Other 2025 reuse goals">
                  <FormattedText
                    inputText={pageData.other_2025_reuse_goals__7_11}
                  />
                </TableRow>
              ) : null}

              {pageData.planned_actions_on_reuse__7_15 ? (
                <TableRow label="Planned actions on reuse">
                  <FormattedText
                    inputText={pageData.planned_actions_on_reuse__7_15}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>

            <HorizontalTable
              title="100% reusable, recyclable and/or compostable plastic packaging"
              isVisible={convertStringToBoolean(
                pageData.rrc_block__display__rrc_display
              )}
            >
              <SubHeaderAtom>
                Progress made over the reporting period
              </SubHeaderAtom>

              <TableRow label="Share of plastic packaging that is reusable, recyclable or compostable (2020)">
                {pageData._2020_reusable_recyclable_or_compostable__percent___4_5_1 !==
                '' ? (
                  <>
                    <PercentageChange
                      currentValue={convertStringToFloat(
                        pageData._2020_reusable_recyclable_or_compostable__percent___4_5_1
                      )}
                      previousValue={
                        pageData._2019_reusable_recyclable_or_compostable__percent___3_2_17
                          ? convertStringToFloat(
                              pageData._2019_reusable_recyclable_or_compostable__percent___3_2_17
                            )
                          : undefined
                      }
                      isDeviated={convertStringToBoolean(
                        pageData.deviation__dev
                      )}
                    />
                    {pageData._2019_reusable_recyclable_or_compostable__percent___3_2_17
                      ? ' from prior year'
                      : null}
                    {convertStringToBoolean(pageData.deviation__dev) && (
                      <>
                        <br />
                        {'See more details in "packaging design assesment"'}
                      </>
                    )}
                  </>
                ) : (
                  <PAtom>
                    {
                      pageData._2020_reusable_recyclable_or_compostable__alternative__4_5_1_excep
                    }
                  </PAtom>
                )}
              </TableRow>

              {pageData.actions_and_progress_made_to_make_packaging_recyclable_or_compostable__see_previous_section_for_progress_on_shifting_to_reusable_packaging___8_1_1_1_1 ? (
                <TableRow label="Actions and progress made to make packaging recyclable or compostable (see previous section  for progress on shifting to reusable packaging)">
                  <FormattedText
                    inputText={
                      pageData.actions_and_progress_made_to_make_packaging_recyclable_or_compostable__see_previous_section_for_progress_on_shifting_to_reusable_packaging___8_1_1_1_1
                    }
                  />
                </TableRow>
              ) : null}

              {pageData.actions_and_progress_made_on_collaboration_towards_increasing_recycling_and_composting_rates_for_plastic_packaging__8_1_1_2_1 ? (
                <TableRow label="Actions and progress made on collaborating towards increasing recycling and composting rates for plastic packaging">
                  <FormattedText
                    inputText={
                      pageData.actions_and_progress_made_on_collaboration_towards_increasing_recycling_and_composting_rates_for_plastic_packaging__8_1_1_2_1
                    }
                  />
                </TableRow>
              ) : null}

              <SubHeaderAtom displaySeparator={true}>
                Additional details on recyclability
              </SubHeaderAtom>

              <TableRow label="Share of plastic packaging that is recyclable (2020)">
                {pageData._2020_recyclable__4_5_24 !== '' ? (
                  <>
                    <PercentageChange
                      currentValue={convertStringToFloat(
                        pageData._2020_recyclable__4_5_24
                      )}
                      previousValue={
                        pageData._2019_recyclable_plastic_packaging__3_2_11
                          ? convertStringToFloat(
                              pageData._2019_recyclable_plastic_packaging__3_2_11
                            )
                          : undefined
                      }
                      isDeviated={convertStringToBoolean(
                        pageData.deviation__dev
                      )}
                    />
                    {pageData._2019_recyclable_plastic_packaging__3_2_11
                      ? ' from prior year'
                      : null}
                  </>
                ) : (
                  <PAtom>
                    {pageData._2020_recyclable_alternative__4_5_24_excep}
                  </PAtom>
                )}
              </TableRow>

              {pageData.alignment_with_the_new_plastics_economy_2021_recycling_rate_survey_results__8_align ? (
                <TableRow label="Alignment with the New Plastics Economy 2021 Recycling Rate Survey results">
                  <FormattedText
                    inputText={
                      pageData.alignment_with_the_new_plastics_economy_2021_recycling_rate_survey_results__8_align
                    }
                  />
                </TableRow>
              ) : null}

              {pageData.optional_metric__plastic_packaging_designed_for_recycling__4_12 ? (
                <TableRow label="Optional metric — share of plastic packaging that is ‘designed for recycling’">
                  <FormattedText
                    inputText={
                      pageData.optional_metric__plastic_packaging_designed_for_recycling__4_12
                    }
                  />
                </TableRow>
              ) : null}

              <SubHeaderAtom displaySeparator={true}>
                Planned actions by 2025
              </SubHeaderAtom>

              {pageData._2025_target__rrc_full_target ? (
                <TableRow label="2025 target">
                  <FormattedText
                    inputText={pageData._2025_target__rrc_full_target}
                  />
                </TableRow>
              ) : pageData._2025_target__rrc_full_target_excep ? (
                <TableRow label="2025 target">
                  <FormattedText
                    inputText={pageData._2025_target__rrc_full_target_excep}
                  />
                </TableRow>
              ) : null}

              {pageData.planned_actions_to_make_packaging_recyclable_or_compostable__8_2_1_1_1 ? (
                <TableRow label="Planned actions to make packaging recyclable or compostable">
                  <FormattedText
                    inputText={
                      pageData.planned_actions_to_make_packaging_recyclable_or_compostable__8_2_1_1_1
                    }
                  />
                </TableRow>
              ) : null}

              {pageData.planned_actions_on_collaborating_towards_increasing_reyclings_and_composting_rates_for_plastic_packaging__8_2_1_2_1 ? (
                <TableRow label="Planned actions on collaborating towards increasing recycling and composting rates for plastic packaging">
                  <FormattedText
                    inputText={
                      pageData.planned_actions_on_collaborating_towards_increasing_reyclings_and_composting_rates_for_plastic_packaging__8_2_1_2_1
                    }
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>

            <HorizontalTable
              title="Increasing post-consumer recycled content in plastic packaging"
              isVisible={convertStringToBoolean(
                pageData.pcr_block__display__pcr_display
              )}
            >
              <SubHeaderAtom>
                Progress made over the reporting period
              </SubHeaderAtom>

              <TableRow label="Level of post-consumer recycled content in plastic packaging (2020)">
                {pageData._2020_postconsumer_recycled_content__percent___4_4_11 !==
                '' ? (
                  <>
                    <PercentageChange
                      currentValue={convertStringToFloat(
                        pageData._2020_postconsumer_recycled_content__percent___4_4_11
                      )}
                      previousValue={
                        pageData._2019_postconsumer_recycled_content__percent___3_2_5
                          ? convertStringToFloat(
                              pageData._2019_postconsumer_recycled_content__percent___3_2_5
                            )
                          : undefined
                      }
                    />
                    {pageData._2019_postconsumer_recycled_content__percent___3_2_5
                      ? ' from prior year'
                      : null}
                  </>
                ) : (
                  <PAtom>
                    {
                      pageData.postconsumer_recycled_content__alternative__4_4_11_excep
                    }
                  </PAtom>
                )}
              </TableRow>

              {pageData.activites_and_progress_made_on_postconsumer_recycled_content__9_1 ? (
                <TableRow label="Activities and progress made on post-consumer recycled content">
                  <FormattedText
                    inputText={
                      pageData.activites_and_progress_made_on_postconsumer_recycled_content__9_1
                    }
                  />
                </TableRow>
              ) : null}

              <SubHeaderAtom displaySeparator={true}>
                Planned actions by 2025
              </SubHeaderAtom>

              <TableRow label="2025 post-consumer recycled content target">
                {pageData.target_postconsumer_recycled_content__percent___pcr_target ? (
                  <PAtom>
                    {`${pageData.target_postconsumer_recycled_content__percent___pcr_target}%`}
                  </PAtom>
                ) : (
                  <PAtom>
                    {
                      pageData.target_postconsumer_recycled_content__alternative__pcr_target_excep
                    }
                  </PAtom>
                )}
              </TableRow>

              {pageData.planned_actions_to_increase_postconsumer_recycled_content__9_2 ? (
                <TableRow label="Planned actions to increase post-consumer recycled content">
                  <FormattedText
                    inputText={
                      pageData.planned_actions_to_increase_postconsumer_recycled_content__9_2
                    }
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="investments" className="link-destination" />

            <HorizontalTable
              title="Investments"
              isVisible={convertStringToBoolean(
                pageData.pcr_block__display__pcr_display
              )}
            >
              <TableRow label="Total invested/committed over the reporting period towards achieving the commitments">
                {pageData.total_invested_committed_over_the_reporting_period__21_1_1 ? (
                  <PAtom>
                    {
                      pageData.total_invested_committed_over_the_reporting_period__21_1_1
                    }
                  </PAtom>
                ) : (
                  <PAtom>
                    {
                      pageData.total_invested_committed_over_the_reporting_period__alternative_text__21_2
                    }
                  </PAtom>
                )}
              </TableRow>

              {pageData.additional_details_on_investments__21_3 ? (
                <TableRow label="Additional details on investments">
                  <FormattedText
                    inputText={pageData.additional_details_on_investments__21_3}
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>

            <div id="end-notes" className="link-destination" />

            <HorizontalTable
              title="End notes"
              isVisible={convertStringToBoolean(
                pageData.endnotes_block__display__endnotes_display
              )}
            >
              {pageData.thirdparty_verification_assurance__2_3 ? (
                <TableRow label="Third-party verification/assurance">
                  <FormattedText
                    inputText={pageData.thirdparty_verification_assurance__2_3}
                  />
                </TableRow>
              ) : null}

              {pageData.scope_of_reporting__2_6 ? (
                <TableRow label="Scope of reporting">
                  <FormattedText inputText={pageData.scope_of_reporting__2_6} />
                </TableRow>
              ) : null}

              {pageData.interpreting_the_quantitative_data_submitted__4_14 ? (
                <TableRow label="Interpreting the quantitative data submitted">
                  <FormattedText
                    inputText={
                      pageData.interpreting_the_quantitative_data_submitted__4_14
                    }
                  />
                </TableRow>
              ) : null}

              {pageData.reason_for_scope_exclusion_in_the_reduction_target_if_any__10_3 ? (
                <TableRow label="Reason for scope exclusion in the reduction target">
                  <FormattedText
                    inputText={
                      pageData.reason_for_scope_exclusion_in_the_reduction_target_if_any__10_3
                    }
                  />
                </TableRow>
              ) : null}

              {pageData.context_to_the_plastic_reduction_target__10_4 ? (
                <TableRow label="Context to the plastic reduction target">
                  <FormattedText
                    inputText={
                      pageData.context_to_the_plastic_reduction_target__10_4
                    }
                  />
                </TableRow>
              ) : null}

              {pageData.recyclability_assessment_evidence_for_alternative_assumptions_used__8_endnotes ? (
                <TableRow label="Alternative assumptions used for the recyclability assessment">
                  <FormattedText
                    inputText={
                      pageData.recyclability_assessment_evidence_for_alternative_assumptions_used__8_endnotes
                    }
                  />
                </TableRow>
              ) : null}

              {pageData.plastic_packaging_designed_for_recycling__4_13 ? (
                <TableRow label="Plastic packaging designed for recycling">
                  <FormattedText
                    inputText={
                      pageData.plastic_packaging_designed_for_recycling__4_13
                    }
                  />
                </TableRow>
              ) : null}

              {pageData.product_line_definition__prod_line_def ? (
                <TableRow label="Product line definition">
                  <FormattedText
                    inputText={pageData.product_line_definition__prod_line_def}
                  />
                </TableRow>
              ) : null}

              {pageData.revenues_to_be_derived_from_reuse_models_by_2025__7_13_1_text ? (
                <TableRow label="Revenues to be derived from reuse models by 2025">
                  <FormattedText
                    inputText={
                      pageData.revenues_to_be_derived_from_reuse_models_by_2025__7_13_1_text
                    }
                  />
                </TableRow>
              ) : null}

              {pageData.units_solds_to_be_derived_from_reuse_models_by_2025__7_14_5_text ? (
                <TableRow label="Units sold to be derived from reuse models by  2025">
                  <FormattedText
                    inputText={
                      pageData.units_solds_to_be_derived_from_reuse_models_by_2025__7_14_5_text
                    }
                  />
                </TableRow>
              ) : null}
            </HorizontalTable>
          </ProfileContainer>
        </SectionLayout>
      </div>
    </Layout>
  );
};

const StyledGcPpuPage = styled(Page)`
  .wrapper {
    margin: 12rem ${vars.spacingL};
  }
  ${PercentageChange} {
    margin: 0;
  }
  ${GroupedTable}:last-of-type {
    margin-bottom: 0.8rem;
  }
`;

export default StyledGcPpuPage;
